import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useApp } from '../store';
import { confirmDialog } from '../confirm';
import RichTextEditor from './RichTextEditor';
import DateField from './DateField';
import { useDismiss, useFocusTrap } from '../motion';
import VendorTag from './VendorTag';
import { EMPTY_RICH_DOC, richHasText, richText, type RichDoc } from '../rich-text';
import { can } from '../permissions';
import { canCreateIssues, type IssueSource } from '../ownership';
import { displayName, initials } from '../people';
import { liveTypes } from '../issue-types';
import { defaultAssignee, assistantsOf } from '../assistant';
import { cardCreateDefault } from '../company-role';
import { boardAssignees, PROJECT_PRIORITIES, rollupBudget, type Stakeholder, type ProjectPriority } from '../projects';
import { parseQuickAdd } from '../quick-add';
import Icon, { type IconName } from './Icon';
import MoscowNudge from './MoscowNudge';
import { useSampleBoard, useSampleInert } from './SampleStrip';
import RenameField from './RenameField';
import Expander from './Expander';
import ProjectGuide from './ProjectGuide';
import LessonsAtStart from './LessonsAtStart';
import BudgetModal from './BudgetModal';
import ApproverPicker from './ApproverPicker';
import InfoTip from './InfoTip';
import MoneyInput from './MoneyInput';
import { fmtMoney } from '../money';
import { isProjectMode } from '../lifecycle';
import {
  projectMilestones,
  milestoneBilling,
  milestonesVsContract,
  milestoneScope,
  MILESTONE_KINDS,
  MILESTONE_KIND_LABELS,
  type Milestone,
  type MilestoneKind,
  type MilestoneState,
  type MilestoneTie,
  type MilestoneRow,
} from '../milestones';
import { milestoneForecast, withMilestoneForecasts, FORECAST_TRIALS_GLANCE } from '../milestone-forecast';
import {
  livePhases,
  phaseCardCounts,
  phaseProgressFromCards,
  phaseDerivedStatus,
  daysPast,
  goLiveState,
  goLiveAnchorDay,
  goLiveIsPlanAnchored,
  GO_LIVE_APPROACHING_DAYS,
  PHASE_STATUSES,
  PHASE_STATUS_LABELS,
  type Phase,
  type PhaseStatus,
} from '../phases';
import type { GoLiveAnchor, Project } from '../projects';
import { STATUS_LABELS, STATUS_ORDER, type Doc, type Issue, type IssueStatus } from '../types';
import { effectiveProgressPct } from '../progress-derive';
import { projectProgress, projectEvm, budgetHealth, budgetOutcomeRag, startDayOf } from '../portfolio';
import { scheduleHealth, expectedPctOfWindow } from '../progress-health';
import { DOCUMENT_STATE, NEW_DOC, covers, isProjectArtifact } from '../documents';
import { DocFace } from './DocLine';

/**
 * THE PLAN — one surface, a view over the board's cards. #377 / projectization.
 *
 * Rebuilt to the approved mock dev-preview/plan-unified-proposal.html and the
 * decisions locked with it. The whole of a project's plan lives here now, not
 * split with Settings: a phase is a row you create, name, re-date, reorder and
 * remove right on this screen (D4), and its work is the board cards that name it
 * (D2) — so a developer moving a card forward is what advances the plan, and the
 * two can never drift. A phase's status is EARNED from those cards (D1): planned
 * until one starts, active while work is underway, done once all are resolved;
 * the PM can override it, and an override is shown as an override so it cannot
 * silently lie. Everything is editable, anytime — best practice is the starting
 * shape, not a cage.
 *
 * This is the R1 core: the LIST view. The read-mostly timeline (D3), the draft →
 * approved baseline (D6) and the discipline/due columns the mock also draws are
 * later work — a token for a field the card has no column for would be a promise
 * the save cannot keep, so it is not here yet.
 */

/** A date input's `yyyy-mm-dd` as milliseconds at UTC midnight — the same day
 *  the header reads back with `dayUTC`, so an edited go-live round-trips to the
 *  exact day it was set, whatever the viewer's timezone. */
function dayToMs(day: string): number | undefined {
  const t = Date.parse(`${day}T00:00:00.000Z`);
  return Number.isNaN(t) ? undefined : t;
}

/** A `YYYY-MM-DD` day as "23 Apr". Empty string for nothing. */
function shortDay(day?: string | null): string {
  if (!day) return '';
  const [y, m, d] = day.split('-').map(Number);
  if (!y || !m || !d) return '';
  return new Date(y, m - 1, d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
}

/** A phase's window, read as [where the last one ended, this one's target]. Only
 *  the target is stored per phase; the start is the phase before it (or the
 *  project's own start), because phases run in sequence. */
function windowStr(start: string | null, end?: string | null): string {
  const a = shortDay(start);
  const b = shortDay(end);
  if (a && b) return `${a} – ${b}`;
  if (b) return `by ${b}`;
  if (a) return `from ${a}`;
  return 'no dates yet';
}

export default function PlanView() {
  const projectId = useApp((s) => s.projectId);
  const projects = useApp((s) => s.projects);
  const project = projects.find((p) => p.id === projectId);
  const profiles = useApp((s) => s.profiles);
  const phases = useApp((s) => s.phases);
  const milestones = useApp((s) => s.milestones);
  const issues = useApp((s) => s.issues);
  const doneChecks = useApp((s) => s.meta.doneChecks);
  const workCalendar = useApp((s) => s.workCalendar);
  const role = useApp((s) => s.shownRole);
  const source = useApp((s) => s.meta.source ?? 'tracker');
  // The cards with their EFFECTIVE % stamped in, computed once — so a phase bar
  // reflects checklist-derived progress, not just typed %s. phaseCardCounts keeps
  // reading the raw issues (it ignores %), only the % roll-up reads this.
  const cardsForProgress = useMemo(
    () => issues.map((i) => ({ ...i, progressPct: effectiveProgressPct(i, doneChecks) })),
    [issues, doneChecks],
  );
  // Base currency + rates, so the summary's Remaining converts foreign lines the
  // same way the budget popup does.
  const budgetBase = useApp((s) => s.currency);
  const budgetRates = useApp((s) => s.fxRates);

  const mayPlan = can(role, 'editSettings');
  const mayAddCards = canCreateIssues(role, source as IssueSource);
  const inert = useSampleInert();
  const updateProject = useApp((s) => s.updateProject);
  const setView = useApp((s) => s.setView);
  const setBudgetEnterEdit = useApp((s) => s.setBudgetEnterEdit);
  const putProjectOnHold = useApp((s) => s.putProjectOnHold);
  const liftProjectHold = useApp((s) => s.liftProjectHold);
  // The charter now lives in Documents (0102); the About block points to it.
  const documents = useApp((s) => s.documents);
  const drillTo = useApp((s) => s.drillTo);
  const draftDocument = useApp((s) => s.draftDocument);

  // The go-live date is set at project creation but there was no way to change it
  // afterwards (#387) — so a board made without one was stuck on "No go-live date
  // set" and the health verdict could never fire. The pencil (or the "+ Set
  // go-live date" button when none is set) opens an inline date field, for
  // whoever may edit settings. Kept in local state so it opens without a save.
  // The go-live anchor editor (0217) — opened from the header pencil / unset button.
  const [anchorEdit, setAnchorEdit] = useState(false);
  // On hold (0115) — the form opens below the header like the budget one.
  const [holdEdit, setHoldEdit] = useState(false);
  const [holdReason, setHoldReason] = useState('');
  const [holdPickup, setHoldPickup] = useState('');

  // Budget management + the ledger live in a popup now (0116). The Plan shows
  // only the always-visible budget SUMMARY; managing it (the baseline, the split,
  // the forecast, and the invoice/commitment ledger) opens here — too many
  // inline-expanding panels made the Plan messy (Vinnie). 'edit' opens the modal
  // straight on the budget form (the "Set budget" / "Add a forecast" on-ramps).
  const [budgetModal, setBudgetModal] = useState<null | 'view' | 'edit'>(null);
  // A specific ledger line to open on arrival — set when the Payments tab on
  // Sign-offs sends the approver straight to the line they must decide. Consumed by
  // BudgetModal, then cleared so it opens once.
  const [focusLineId, setFocusLineId] = useState<string | null>(null);
  // The ledger rolled up — the header's Actual is now the paid total (the sum of
  // paid lines), and Committed the raised-but-unpaid total. The forecast tripwire
  // lives inside the ledger drawer (BudgetLedger).
  const budgetRoll = useMemo(
    () => rollupBudget(project ?? {}, { base: budgetBase, rates: budgetRates }),
    [project, budgetBase, budgetRates],
  );
  const hasLines = (project?.invoices?.length ?? 0) > 0;
  // The budget verdict + variance are DERIVED from the earned-value read, not the hand-typed
  // forecast — computed below once the go-live is resolved, so the Plan reads the SAME estimate
  // at completion the Portfolio and the status drill do (SB-1). See `planEvm` after `goLiveDay`.

  // ABOUT THIS PROJECT — the project's METADATA (#387) plus the vendor (0100),
  // and a pointer to the charter document. The go-live, type and budget sit in
  // the header; this block holds who is undertaking what, who runs it, who
  // sponsors it, the vendor and contract, and the stakeholders. The charter
  // NARRATIVE — scope in/out, milestones, assumptions, constraints, the risk
  // register, RACI and sign-off — is no longer typed into plain boxes here: it is
  // authored as a real charter DOCUMENT (0102), so this block links to it rather
  // than duplicating it. Collapsed by default so the phases stay the focus.
  const [aboutOpen, setAboutOpen] = useState(false);
  const [aboutEdit, setAboutEdit] = useState(false);
  const [aboutProblem, setAboutProblem] = useState('');
  // Autosave plumbing for Project details (finding #1): a debounce timer, a flag
  // that marks the load-from-project render so it does not count as an edit, and
  // the Saving…/Saved status shown where the Save button used to be.
  const aboutTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const aboutHydrating = useRef(false);
  const [aboutStatus, setAboutStatus] = useState<'idle' | 'saving' | 'saved'>('idle');
  // The "who's accountable" nudge (below) asks the planner to name the project
  // manager. Its button opens Project details and lands on that field; this flag
  // fires the reveal once the edit form has rendered.
  const [nudgeToPM, setNudgeToPM] = useState(false);
  const [aDesc, setADesc] = useState<RichDoc>(EMPTY_RICH_DOC);
  const [aPM, setAPM] = useState('');
  const [aSponsor, setASponsor] = useState('');
  // The designated payment approver (#invoice-governance). A real person, not free
  // text like the fields around it, because eligibility (board manager/owner or
  // admin) can only be checked against an account. Set via ApproverPicker.
  const [aPayApprover, setAPayApprover] = useState('');
  const [approverPicking, setApproverPicking] = useState(false);
  const [aVendor, setAVendor] = useState('');
  const [aContract, setAContract] = useState('');
  const [aVendorContact, setAVendorContact] = useState('');
  const [aStake, setAStake] = useState<Stakeholder[]>([]);
  const [aSuccess, setASuccess] = useState<RichDoc>(EMPTY_RICH_DOC);
  const [aPriority, setAPriority] = useState<ProjectPriority | ''>('');
  // Scope, assumptions and constraints are rich text (0113) — a PM writes them as
  // bullet lists, so they are the shared editor, not plain boxes.
  const [aScopeIn, setAScopeIn] = useState<RichDoc>(EMPTY_RICH_DOC);
  const [aScopeOut, setAScopeOut] = useState<RichDoc>(EMPTY_RICH_DOC);
  const [aAssumptions, setAAssumptions] = useState<RichDoc>(EMPTY_RICH_DOC);
  const [aConstraints, setAConstraints] = useState<RichDoc>(EMPTY_RICH_DOC);
  const openAboutEdit = () => {
    // Loading the form from the project is not an edit — the autosave effect must
    // not fire on the state-sets below, so mark this render as hydrating.
    aboutHydrating.current = true;
    setAboutStatus('idle');
    setADesc(project?.description ?? EMPTY_RICH_DOC);
    setAPM(project?.projectManager ?? '');
    setASponsor(project?.sponsor ?? '');
    setAPayApprover(project?.payApprover ?? '');
    setAVendor(project?.vendor ?? '');
    setAContract(project?.contractRef ?? '');
    setAVendorContact(project?.vendorContact ?? '');
    setAStake(project?.stakeholders?.length ? project.stakeholders.map((s) => ({ ...s })) : []);
    setASuccess(project?.successMeasure ?? EMPTY_RICH_DOC);
    setAPriority(project?.priority ?? '');
    setAScopeIn(project?.scopeIn ?? EMPTY_RICH_DOC);
    setAScopeOut(project?.scopeOut ?? EMPTY_RICH_DOC);
    setAAssumptions(project?.assumptions ?? EMPTY_RICH_DOC);
    setAConstraints(project?.constraints ?? EMPTY_RICH_DOC);
    setAboutOpen(true);
    setAboutEdit(true);
  };
  // Project details AUTOSAVES, like the charter (finding #1): every edit persists
  // on a debounce, so nothing is lost on click-away — there is no Save button to
  // forget. `commitAbout` writes the whole form; every field is written, empty
  // included, so blanking one clears it. Rich-text fields send `null` to clear
  // (updateProject drops `undefined` as leave-alone but writes `null`).
  const commitAbout = useCallback(() => {
    const stake = aStake
      .map((s) => ({ name: s.name.trim(), role: s.role?.trim() || undefined }))
      .filter((s) => s.name);
    return updateProject(projectId, {
      // "What we're undertaking" is rich text now (0134), like the Scope fields.
      description: richHasText(aDesc) ? aDesc : null,
      projectManager: aPM.trim(),
      sponsor: aSponsor.trim(),
      // A person, resolved by the picker. Empty clears it — approvals then fall
      // back to an admin or manager (it is recommended, not required).
      payApprover: aPayApprover.trim() || undefined,
      vendor: aVendor.trim(),
      contractRef: aContract.trim(),
      vendorContact: aVendorContact.trim(),
      stakeholders: stake,
      // The expected outcome is rich text now (0134); the project's own priority
      // is still a plain pick. undefined leaves priority alone, not a stale value.
      successMeasure: richHasText(aSuccess) ? aSuccess : null,
      priority: aPriority || undefined,
      // Rich-text charter fields (0113/0134): a blanked section must CLEAR, so an
      // empty document sends explicit null. Sending undefined here was the
      // "empty Scope won't clear" bug: the column kept its old value on reload.
      scopeIn: richHasText(aScopeIn) ? aScopeIn : null,
      scopeOut: richHasText(aScopeOut) ? aScopeOut : null,
      assumptions: richHasText(aAssumptions) ? aAssumptions : null,
      constraints: richHasText(aConstraints) ? aConstraints : null,
    });
  }, [
    projectId,
    updateProject,
    aDesc,
    aSuccess,
    aPM,
    aSponsor,
    aPayApprover,
    aVendor,
    aContract,
    aVendorContact,
    aStake,
    aPriority,
    aScopeIn,
    aScopeOut,
    aAssumptions,
    aConstraints,
  ]);

  // Keep the newest committer in a ref so an unmount can flush a pending save
  // without the flush effect re-running on every edit.
  const commitAboutRef = useRef(commitAbout);
  commitAboutRef.current = commitAbout;

  // Debounced autosave: whenever a field changes while the editor is open, save
  // 900ms after the last keystroke (the charter's cadence). The hydrating guard
  // skips the state-sets that `openAboutEdit` makes when it loads the form.
  useEffect(() => {
    if (!aboutEdit) return;
    if (aboutHydrating.current) {
      aboutHydrating.current = false;
      return;
    }
    setAboutStatus('saving');
    if (aboutTimer.current) clearTimeout(aboutTimer.current);
    aboutTimer.current = setTimeout(() => {
      void commitAbout();
      aboutTimer.current = null;
      setAboutStatus('saved');
    }, 900);
    return () => {
      if (aboutTimer.current) clearTimeout(aboutTimer.current);
    };
  }, [commitAbout, aboutEdit]);

  // If the view unmounts mid-debounce, flush the pending save so no edit is lost.
  useEffect(
    () => () => {
      if (aboutTimer.current) {
        clearTimeout(aboutTimer.current);
        aboutTimer.current = null;
        void commitAboutRef.current();
      }
    },
    []
  );

  // "Done" collapses the editor; flush any pending save first so the last edit
  // lands before the form closes.
  const closeAbout = () => {
    if (aboutTimer.current) {
      clearTimeout(aboutTimer.current);
      aboutTimer.current = null;
      void commitAbout();
    }
    setAboutStatus('idle');
    setAboutEdit(false);
  };

  // ARRIVED FROM A "Set it in the project's About →" link — the requirements
  // document sends the reviewer straight here, so the About editor opens on
  // arrival and nobody has to hunt for it. Same revealId pattern the Documents
  // and Sprints screens use. The ref fires it once per drill.
  const revealId = useApp((s) => s.revealId);
  const revealedAbout = useRef(false);
  useEffect(() => {
    // Both the About strip and the single-approver control live INSIDE the
    // Project-details editor, so a drill to either must open that editor or its
    // anchor is never in the DOM to find — "set who approves payments" → the
    // single-approver way used to land on the Plan and flash nothing
    // (interaction-witness #2, 15 Oct 2026).
    if ((revealId !== 'about' && revealId !== 'pay-approvers') || revealedAbout.current) return;
    revealedAbout.current = true;
    if (mayPlan) openAboutEdit();
    else setAboutOpen(true);
    // Land somewhere the eye can find: scroll the target into view and flash it
    // briefly, so the link that sent us here doesn't drop the reader on a strip
    // they have to hunt for (the title alone used to be too quiet to notice). The
    // pay-approvers anchor only exists once the editor we just opened has
    // rendered, so find it on the next tick rather than this one.
    const want = revealId;
    const flash = () => {
      const el =
        want === 'pay-approvers'
          ? document.querySelector<HTMLElement>('[data-reveal="pay-approvers"]')
          : document.getElementById('plan-about');
      el?.scrollIntoView({ block: 'start', behavior: 'smooth' });
      if (el) {
        el.classList.add('just-revealed');
        window.setTimeout(() => el.classList.remove('just-revealed'), 1800);
      }
    };
    if (want === 'pay-approvers') window.setTimeout(flash, 60);
    else flash();
  }, [revealId, mayPlan]);

  // ARRIVED FROM THE PAYMENTS TAB ON SIGN-OFFS — "Review & approve" sends the
  // approver straight to the line they must decide: open the budget modal on the
  // ledger and hand the line id to it so the drawer opens on that line. Same
  // revealId pattern, keyed `budget:<lineId>`; the ref fires it once per drill.
  const revealedBudget = useRef(false);
  useEffect(() => {
    if (!revealId || !revealId.startsWith('budget:') || revealedBudget.current) return;
    revealedBudget.current = true;
    setFocusLineId(revealId.slice('budget:'.length));
    setBudgetModal('view');
  }, [revealId]);

  // THE ACCOUNTABILITY NUDGE'S REVEAL — the same mechanism the deep-link above
  // uses, but fired from the "Name the project manager" button rather than an
  // arriving link. openAboutEdit has opened Project details in edit mode; once
  // that form has rendered we scroll to the Project manager field, flash the
  // section (the shared just-revealed tint) and land the cursor in the input, so
  // the button genuinely takes the person to the field, not just near it.
  useEffect(() => {
    if (!nudgeToPM || !aboutOpen || !aboutEdit) return;
    setNudgeToPM(false);
    const about = document.getElementById('plan-about');
    const field = document.getElementById('plan-pm-field');
    (field ?? about)?.scrollIntoView({ block: 'center', behavior: 'smooth' });
    if (about) {
      about.classList.add('just-revealed');
      window.setTimeout(() => about.classList.remove('just-revealed'), 1800);
    }
    field?.querySelector('input')?.focus({ preventScroll: true });
  }, [nudgeToPM, aboutOpen, aboutEdit]);

  // THE CHARTER DOCUMENT this project points to. A project may have several over
  // its life (a signed one, then a superseded predecessor); the live one is what
  // the block links to, newest first, falling back to a superseded copy if that
  // is all there is. Only charters are considered — the other document kinds have
  // their own homes on the Documents screen.
  const charterDoc = useMemo(() => {
    const charters = documents.filter((d) => d.kind === 'charter');
    const live = charters.filter((d) => d.state !== 'superseded');
    return (live.length ? live : charters).sort(
      (a, b) => (b.createdAt ?? 0) - (a.createdAt ?? 0)
    )[0];
  }, [documents]);
  const openCharter = () => {
    if (charterDoc) drillTo('documents', charterDoc.id);
  };
  // START A CHARTER — draft the document (seeded with the template, 0102) and go
  // straight into it on the Documents screen. Managers only, matching who may
  // create documents; the database enforces the same.
  const startCharter = async () => {
    setAboutProblem('');
    const { id, problem } = await draftDocument('charter', 'Project charter');
    if (problem) {
      setAboutProblem(problem);
      return;
    }
    drillTo('documents', id);
  };

  const live = useMemo(() => livePhases(phases), [phases]);
  const retired = useMemo(
    () => phases.filter((p) => p.retiredAt).sort((a, b) => a.position - b.position),
    [phases]
  );

  // The cards under each phase, in a steady reading order. Grouped once so a
  // long board is not re-scanned for every phase row.
  const cardsByPhase = useMemo(() => {
    const m = new Map<string, Issue[]>();
    for (const i of issues) {
      if (!i.phase) continue;
      const arr = m.get(i.phase);
      if (arr) arr.push(i);
      else m.set(i.phase, [i]);
    }
    for (const arr of m.values()) {
      arr.sort((a, b) => STATUS_ORDER.indexOf(a.status) - STATUS_ORDER.indexOf(b.status));
    }
    return m;
  }, [issues]);

  // The one headline number: the WEIGHTED project completion — each card earns the share
  // it records (its %/checklist/DoD), the SAME projectProgress() the portfolio and status
  // report show, so the Plan never reads a different "% complete" than the status drill
  // (OBS-3, 30 Oct). total/done stay as the phased-card count for the visibility guard.
  const overall = useMemo(() => {
    let total = 0;
    let done = 0;
    for (const p of live) {
      const c = phaseCardCounts(issues, p.id);
      total += c.total;
      done += c.done;
    }
    return { total, done, pct: projectProgress(issues, doneChecks ?? []).pct };
  }, [live, issues, doneChecks]);

  // The project's start and go-live are millisecond timestamps set to a day's
  // UTC midnight; read the day back in UTC so a western timezone cannot slide it
  // to the day before, the way every other date on this screen is a plain day.
  const dayUTC = (ms: number) => new Date(ms).toISOString().slice(0, 10);
  const startDay = project?.startDate ? dayUTC(project.startDate) : null;
  // The go-live as a plain YYYY-MM-DD day, resolved through its anchor (0217): a phase anchor
  // reads the phase's target end, a milestone anchor that milestone's day, a fixed/unset anchor
  // the typed date. The form goLiveState and every phase row read against this, so the chip,
  // the date and the verdict all agree. `planAnchored` says a phase ending after it is calm
  // post-launch work, not a breach.
  const milestoneDay = useCallback(
    (id: string): string | null | undefined => milestones.find((m) => m.id === id)?.targetDay,
    [milestones],
  );
  const goLiveDay = project ? goLiveAnchorDay(project, live, milestoneDay) : undefined;
  const planAnchored = goLiveIsPlanAnchored(project?.goLiveAnchor);
  const goLive = goLiveDay ? shortDay(goLiveDay) : '';

  // THE DERIVED EARNED-VALUE READ — the SAME assembly the Portfolio and the status drill use
  // (projectEvm), so the Plan's budget verdict can never disagree with them (SB-1). The estimate
  // at completion follows the board's pace and spend; the hand-typed forecast is named beside it,
  // not the thing the verdict reads.
  const planEvm = useMemo(() => {
    if (!project) return null;
    const pp = projectProgress(issues, doneChecks ?? []);
    const expected = expectedPctOfWindow(startDayOf(project), goLiveDay ?? null, Date.now(), workCalendar);
    return projectEvm({ project, pp, expectedThroughPlan: expected, roll: budgetRoll, now: Date.now(), cal: workCalendar, ctx: { base: budgetBase, rates: budgetRates } });
  }, [project, issues, doneChecks, goLiveDay, workCalendar, budgetRoll, budgetBase, budgetRates]);
  // The estimate the verdict reads: the derived EAC once the cost read is ready (budgetHealth
  // non-null — exactly when the drill shows it), else the manager's hand-typed forecast as the
  // early stand-in. So the Plan matches the drill where both have the derived read, and still
  // shows the manager's own number before there is enough spend to derive one.
  const derivedEac = planEvm && budgetHealth(planEvm) ? planEvm.eac : null;
  const verdictEac = derivedEac ?? project?.forecast ?? null;
  const variance =
    project?.budget != null && project.budget > 0 && verdictEac != null
      ? { diff: verdictEac - project.budget, pct: ((verdictEac - project.budget) / project.budget) * 100 }
      : null;
  const budgetVerdict = useMemo(() => {
    const bud = project?.budget;
    if (bud == null || bud <= 0 || verdictEac == null) return null;
    const pct = (verdictEac / bud - 1) * 100;
    const mag = Math.abs(pct).toFixed(1);
    const title = `${derivedEac != null ? 'Est. at completion' : 'Forecast'} ${fmtMoney(verdictEac)} vs baseline ${fmtMoney(bud)}`;
    // When the derived EAC drives, grade it the SAME way the drill/portfolio pill does
    // (budgetOutcomeRag), so the three never show different colours; the manager-forecast
    // stand-in keeps the Plan's own ±5%/12% bands.
    if (derivedEac != null) {
      const rag = budgetOutcomeRag(derivedEac, bud);
      if (rag === 'red') return { level: 'past' as const, text: `${mag}% over`, title };
      if (rag === 'amber') return { level: 'risk' as const, text: `${mag}% over`, title };
      return { level: 'ok' as const, text: pct < -3 ? `${mag}% under` : 'on track', title };
    }
    if (pct > 12) return { level: 'past' as const, text: `${mag}% over`, title };
    if (pct > 5) return { level: 'risk' as const, text: `${mag}% over`, title };
    if (pct < -5) return { level: 'ok' as const, text: `${mag}% under`, title };
    return { level: 'ok' as const, text: 'on track', title };
  }, [project?.budget, verdictEac, derivedEac]);
  // How the go-live is anchored, in plain words, for the header sub-line (0217). A plan anchor
  // names the phase/milestone it rides on; a fixed date (or a pre-0217 typed date with no anchor
  // yet) reads "committed date · not anchored", nudging the planner to anchor it to the plan.
  const anchorPhaseName =
    project?.goLiveAnchor?.kind === 'phase' && project.goLiveAnchor.id
      ? live.find((p) => p.id === project.goLiveAnchor!.id)?.name
      : undefined;
  const anchorMsName =
    project?.goLiveAnchor?.kind === 'milestone' && project.goLiveAnchor.id
      ? milestones.find((m) => m.id === project.goLiveAnchor!.id)?.name
      : undefined;
  const anchorDesc: string | null =
    project?.goLiveAnchor?.kind === 'phase'
      ? anchorPhaseName
        ? `ends ${anchorPhaseName} phase`
        : 'end of a phase'
      : project?.goLiveAnchor?.kind === 'milestone'
        ? anchorMsName ?? 'a milestone'
        : goLiveDay != null
          ? 'committed date · not anchored'
          : null;
  const anchorIsPlan = planAnchored;
  // The frozen COMMITTED baseline (0217 / pm R1a): once a plan anchor has carried the live
  // go-live day off the date the project committed to, show the promise beside the plan — so a
  // reader sees "tracking 20 Nov · committed 15 Jan", and the as-delivered record (which measures
  // against this committed date) can never silently disagree with the header.
  const committedDay =
    project?.goLive != null ? new Date(project.goLive).toISOString().slice(0, 10) : undefined;
  const committedDiffers = planAnchored && committedDay != null && goLiveDay != null && committedDay !== goLiveDay;

  // On hold (0115) — a parked project reads as its own state, not a schedule or
  // budget verdict, so the header swaps to it and the alarm verdicts are hushed.
  const onHold = !!project?.heldAt;
  const heldDays = project?.heldAt ? Math.max(0, Math.floor((Date.now() - project.heldAt) / 86_400_000)) : 0;
  const confirmHold = () => {
    if (!holdReason.trim()) return;
    void putProjectOnHold(projectId, holdReason, holdPickup);
    setHoldEdit(false);
    setHoldReason('');
    setHoldPickup('');
  };

  // THE HEALTH VERDICT — the one line a sponsor asks for first, escalated so the
  // loudest true thing wins. A go-live breach (a phase past the go-live, or planned
  // to end after it) outranks a plain target slip, which outranks an approaching
  // go-live. All read from the DERIVED status the rows below show — never the stored
  // one — and through the same goLiveState the phase chips use, so the header can
  // never disagree with the phases beneath it.
  const verdict = useMemo(() => {
    const rows = live.map((p) => {
      const derived = phaseDerivedStatus(issues, p.id);
      const shown = p.status !== 'planned' && p.status !== derived ? p.status : derived;
      return { phase: p, shown, gl: goLiveState(p, goLiveDay, shown, Date.now(), planAnchored) };
    });
    const hasTargets = live.some((p) => p.targetEnd);
    const past = rows.filter((r) => r.gl === 'past-open');
    if (past.length) return { level: 'past' as const, count: past.length, hasTargets };
    const breach = rows
      .filter((r) => r.gl === 'planned-breach')
      .map((r) => {
        const tMs = dayToMs(r.phase.targetEnd!);
        return { phase: r.phase, days: tMs ? daysPast(goLiveDay!, tMs) : 0 };
      })
      .sort((a, b) => b.days - a.days);
    if (breach.length) return { level: 'breach' as const, worst: breach[0], count: breach.length, hasTargets };
    const behind = rows
      .filter((r) => r.shown === 'active')
      .map((r) => ({ phase: r.phase, days: Math.max(0, daysPast(r.phase.targetEnd)) }))
      .filter((x) => x.days > 0)
      .sort((a, b) => b.days - a.days);
    if (behind.length) return { level: 'behind' as const, worst: behind[0], hasTargets };
    if (rows.some((r) => r.gl === 'approaching')) return { level: 'approaching' as const, hasTargets };
    return { level: 'ok' as const, hasTargets };
  }, [live, issues, goLiveDay, planAnchored]);

  // Whether the charter narrative holds anything — decides if the block is shown
  // at all to someone who cannot edit (no empty block for a reader), and drives
  // the one-line peek shown beside the title while it is collapsed.
  const aboutStake = project?.stakeholders?.filter((s) => s.name?.trim()) ?? [];
  const hasAbout = !!(
    richHasText(project?.description) ||
    richHasText(project?.successMeasure) ||
    project?.projectManager?.trim() ||
    project?.sponsor?.trim() ||
    project?.vendor?.trim() ||
    project?.contractRef?.trim() ||
    project?.vendorContact?.trim() ||
    aboutStake.length ||
    richHasText(project?.scopeIn) ||
    richHasText(project?.scopeOut) ||
    richHasText(project?.assumptions) ||
    richHasText(project?.constraints)
  );
  const aboutPeek =
    project?.vendor?.trim() ||
    richText(project?.description) ||
    project?.projectManager?.trim() ||
    project?.sponsor?.trim() ||
    '';

  return (
    <div className="plan">
      <header className="plan-head">
        {/* No page title here — the topbar already names this screen "Plan".
            A second "Plan" heading right beneath it was the word twice (and two
            page headings on one screen); the lead stands on its own. */}
        <p className="plan-lead">
          The stages this project runs in, worked as one surface — each phase is the board cards that
          name it, so its progress is the real work and nothing is entered twice. Create, rename,
          re-date, reorder or remove a phase right here; a phase&rsquo;s status follows its cards, and
          you can override it when you need to.
        </p>
      </header>

      <div className="panel plan2">
        <div className="plan2-top">
          <div className="plan2-top-l">
            <span className="plan2-type">
              {project?.projectType ?? 'Project plan'}
              {/* Project priority, surfaced (0108 slice) — it was captured at
                  creation and shown nowhere. A chip for the ones that carry a
                  signal; medium is the neutral default, so it stays quiet. */}
              {project?.priority && project.priority !== 'medium' && (
                <span className={`plan2-prio ${project.priority}`}>
                  {PROJECT_PRIORITIES.find((p) => p.value === project.priority)?.label ?? project.priority} priority
                </span>
              )}
            </span>
            {/* Go-Live, ANCHORED to the plan (#387, 0217). The date is derived from the anchor
                (a phase end / a milestone), shown with a link glyph and the anchor it rides on,
                and the pencil opens the anchor editor to re-point it. A fixed/committed date (or a
                pre-0217 typed date not yet anchored) reads "committed date · not anchored" and
                nudges toward anchoring. When nothing is set, a dashed "+ Anchor Go-Live to the
                plan" button. The anchor editor is the one way in; the old inline date field is
                gone, so a loose date can't be typed without a choice of basis. */}
            <span className="plan2-meta">
              {goLive ? (
                <span className="plan2-golive">
                  Go-Live {goLive}
                  {anchorDesc && (
                    <span className={`plan2-golive-anchor${anchorIsPlan ? ' is-plan' : ''}`} title={anchorIsPlan ? 'Go-Live is anchored to the plan — its date follows this' : 'A committed date, not anchored to the plan'}>
                      <Icon name={anchorIsPlan ? 'link' : 'info'} size={11} />
                      {anchorDesc}
                    </span>
                  )}
                  {committedDiffers && (
                    <span className="plan2-golive-committed" title="The date this project committed to — the plan now tracks a different day, and the as-delivered record is measured against this committed date">
                      committed {shortDay(committedDay!)}
                    </span>
                  )}
                  {mayPlan && (
                    <button
                      aria-label="Edit how Go-Live is anchored"
                      {...inert('plan2-golive-edit', () => setAnchorEdit(true), 'Change how Go-Live is anchored')}
                    >
                      <Icon name="pencil" size={12} />
                    </button>
                  )}
                </span>
              ) : mayPlan ? (
                <button {...inert('plan2-setgolive', () => setAnchorEdit(true))}>
                  + Anchor Go-Live to the plan
                </button>
              ) : (
                'No Go-Live date set'
              )}
            </span>
            {/* Budget (#387, 0108, 0116) — only the always-visible SUMMARY sits on
                the Plan: the baseline, what's still remaining against it, the
                forecast and the variance. Managing it (the split, the forecast,
                and the invoice/commitment ledger) opens in a popup, so the Plan
                is not a stack of expanded panels (Vinnie). "+ Set budget" for
                those who may plan, nothing to someone who can neither see nor set. */}
            {project?.budget != null ? (
              <span className="plan2-budget">
                <b>Budget</b> <span className="plan2-budget-amt-v">{fmtMoney(project.budget)}</span>
                {budgetRoll.remaining != null && (budgetRoll.actualTotal > 0 || budgetRoll.committedTotal > 0) && (
                  <span>· <b>Remaining</b> <span className={`plan2-budget-amt-v ${budgetRoll.remaining < 0 ? 'over' : ''}`}>{fmtMoney(budgetRoll.remaining)}</span></span>
                )}
                {derivedEac != null ? (
                  <>
                    <span title="Projected final cost, read from the board's own pace and the money already spent — the same figure the Portfolio and the status drill show.">· <b>Est. at completion</b> <span className="plan2-budget-amt-v">{fmtMoney(derivedEac)}</span></span>
                    {project.forecast != null && (
                      <span className="plan2-budget-mgr" title="The manager's own hand-typed estimate at completion.">· Manager's estimate {fmtMoney(project.forecast)}</span>
                    )}
                  </>
                ) : project.forecast != null ? (
                  <span>· <b>Forecast</b> <span className="plan2-budget-amt-v">{fmtMoney(project.forecast)}</span></span>
                ) : null}
                {variance && (
                  <span className={`plan2-var ${variance.diff > 0 ? 'over' : 'under'}`}>
                    {variance.diff > 0 ? '+' : '−'}{fmtMoney(Math.abs(variance.diff))} · {Math.abs(variance.pct).toFixed(1)}% {variance.diff > 0 ? 'over' : 'under'}
                  </span>
                )}
                <button className="plan2-budget-manage" data-reveal="budget" onClick={() => setView('budgetpay')}>
                  Manage budget{hasLines ? ` · ${project.invoices!.length}` : ''}
                </button>
                {mayPlan && project.budget > 0 && project.forecast == null && (
                  <button
                    className="plan2-setforecast"
                    onClick={() => { setBudgetEnterEdit(true); setView('budgetpay'); }}
                    title="Add an estimate at completion to see how the project is tracking against its baseline"
                  >
                    + Add a forecast
                  </button>
                )}
              </span>
            ) : mayPlan ? (
              <span className="plan2-budget">
                <button className="plan2-setbudget" data-reveal="budget" onClick={() => { setBudgetEnterEdit(true); setView('budgetpay'); }}>
                  + Set budget
                </button>
              </span>
            ) : null}
          </div>
          <div className="plan2-top-r">
            {/* On hold wins the header: a parked project is not at risk, it is
                deliberately paused, so the schedule and budget verdicts below are
                hushed while it holds and this single state stands in their place. */}
            {onHold ? (
              <span
                className="plan2-verdict hold"
                title={project?.holdPickup ? `Picked up ${project.holdPickup}` : 'On hold, not at risk'}
              >
                On hold{heldDays > 0 ? ` · ${heldDays}d` : ''}
              </span>
            ) : verdict.level === 'past' ? (
              <span
                className="plan2-verdict past"
                title="Unfinished work has run past the Go-Live date"
              >
                Past Go-Live · {verdict.count} {verdict.count === 1 ? 'phase' : 'phases'} open
              </span>
            ) : verdict.level === 'breach' ? (
              <span
                className="plan2-verdict risk"
                title="A phase is planned to finish after the Go-Live date"
              >
                Plan misses Go-Live · {verdict.worst.phase.name} {verdict.worst.days}d over
              </span>
            ) : verdict.level === 'behind' ? (
              <span
                className="plan2-verdict risk"
                title="A live phase has run past its target"
              >
                At risk · {verdict.worst.phase.name} {verdict.worst.days}d behind
              </span>
            ) : verdict.level === 'approaching' ? (
              <span
                className="plan2-verdict soon"
                title={`The Go-Live is within ${GO_LIVE_APPROACHING_DAYS} days and work is still open`}
              >
                Go-Live approaching
              </span>
            ) : (
              verdict.hasTargets && (
                <span className="plan2-verdict ok" title="Every live phase is within its target and clear of the Go-Live">
                  On track
                </span>
              )
            )}
            {/* The budget verdict sits beside the schedule one, same pill, so the
                two read as co-equal (pm-witness R1). Only when there's a forecast
                to judge against. */}
            {!onHold && budgetVerdict && (
              <span className={`plan2-verdict ${budgetVerdict.level}`} title={budgetVerdict.title}>
                <span className="vlabel">Budget</span>{budgetVerdict.text}
              </span>
            )}
            {overall.total > 0 && (
              <div className="plan2-overall" title="Project complete — each card weighted by its progress (the same % the status report shows)">
                <span className="plan2-overall-pct">{overall.pct}%</span>
                <span className="plan2-obar">
                  <i style={{ width: `${overall.pct}%` }} />
                </span>
              </div>
            )}
            {/* Parking is a decision, so it is an action here rather than a field
                on the details form. Only a planner sees it, and only while the
                project is running — taking it off hold lives on the banner. */}
            {mayPlan && !onHold && (
              <button
                {...inert('plan2-hold-set', () => setHoldEdit((v) => !v), 'Park this project — it drops out of the at-risk view')}
              >
                Put on hold
              </button>
            )}
          </div>
        </div>

        {/* ON HOLD — the parked state, stated plainly with why and when it will
            be picked up. It stands above the plan so nobody reads the phases
            below as live work that has stalled. Only a planner can lift it. */}
        {onHold && (
          <div className="plan2-hold-banner">
            <div className="phb-l">
              <span className="phb-label">On hold</span>
              {heldDays > 0 && <span className="phb-since">for {heldDays} {heldDays === 1 ? 'day' : 'days'}</span>}
              {project?.holdReason && <span className="phb-why">{project.holdReason}</span>}
              {project?.holdPickup && (
                <span className="phb-pickup">Pick up {project.holdPickup}</span>
              )}
            </div>
            {mayPlan && (
              <button {...inert('phb-lift', () => void liftProjectHold(projectId))}>
                Take off hold
              </button>
            )}
          </div>
        )}

        {/* The hold form — opens below the header like the budget one. The reason
            is required (an unexplained hold is the thing this fixes); the pick-up
            is free text, the way a card's parked blocker asks. */}
        {holdEdit && !onHold && (
          <div className="plan2-hold-panel">
            <div className="pbp-head">Put this project on hold</div>
            <p className="pbp-hint">
              While it&rsquo;s on hold, the schedule and budget health are set aside — it reads as On hold
              here and on the portfolio, with your reason and when you&rsquo;ll pick it up.
            </p>
            <div className="phf-grid">
              <label className="phf-field">
                <span className="phf-lab">Why is it on hold?<i className="req">*</i></span>
                <input
                  className="phf-input"
                  autoFocus
                  value={holdReason}
                  placeholder="e.g. team moved to the Wi-Fi rollout"
                  onChange={(e) => setHoldReason(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') confirmHold();
                    if (e.key === 'Escape') setHoldEdit(false);
                  }}
                />
              </label>
              <label className="phf-field">
                <span className="phf-lab">Pick up when?</span>
                <input
                  className="phf-input"
                  value={holdPickup}
                  placeholder="e.g. after the launch"
                  onChange={(e) => setHoldPickup(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') confirmHold();
                    if (e.key === 'Escape') setHoldEdit(false);
                  }}
                />
                <span className="phf-hint">A date, or a few words.</span>
              </label>
            </div>
            <div className="phf-actions">
              <button className="phf-go" disabled={!holdReason.trim()} onClick={confirmHold}>
                Put on hold
              </button>
              <button className="phf-cancel" onClick={() => setHoldEdit(false)}>
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* THE BUDGET MODAL (0116) — budget management (baseline, split, forecast)
            AND the invoice/commitment ledger, in a popup. The Plan keeps only the
            summary above; the heavy management lives here, so the Plan isn't a
            stack of expanded panels (Vinnie). */}
        {budgetModal && (
          <BudgetModal
            startEditing={budgetModal === 'edit'}
            focusLineId={focusLineId}
            onFocusConsumed={() => setFocusLineId(null)}
            onClose={() => { setBudgetModal(null); setFocusLineId(null); }}
          />
        )}

        {/* THE GO-LIVE ANCHOR EDITOR (0217) — point the go-live at the work that delivers it. */}
        {anchorEdit && project && (
          <GoLiveAnchorModal
            project={project}
            projectId={projectId}
            phases={live}
            milestones={milestones}
            issues={issues}
            onClose={() => setAnchorEdit(false)}
          />
        )}

        {/* THE PROJECT GUIDE (#4) — the connective tissue: the real stages, where
            each stands, and a link straight to where the work is done. Sits at the
            top of the project's home so the first thing a project shows is what to
            do next, not a blank plan. Dismissible per viewer. */}
        {/* GETTING STARTED — the two guidance panels share one quiet band and the
            Plan's own disclosure grammar, so they read as one orientation unit of a
            piece with the plan sections below, not a floating card bolted on top
            (Vinnie, 21 Oct 2026). Lessons first, so a new project first meets what
            past projects learned; both collapsed by default for a calm Plan on open.
            The guide always renders on a project's Plan, so the band is never empty. */}
        <div className="gg">
          <div className="gg-eyebrow">Getting started</div>
          <LessonsAtStart />
          <ProjectGuide />
        </div>

        {/* WHO'S ACCOUNTABLE — the second half of the north star. The board's
            owner already defaults to its creator, but delivery accountability is
            the project manager (a named person), and most people skip that field
            — so the portfolio's Owner column silently falls back to the creator.
            This nudge sits directly above Project details, so its button can open
            that section and land the eye on the Project manager field. It is shown
            only to someone who can actually set it, and only while the field is
            blank: filling it in makes the nudge disappear on its own, with no
            separate dismiss to drift out of sync with the data. */}
        {mayPlan && project && !project.projectManager?.trim() && (
          <div className="plan2-owner-nudge">
            <p className="plan2-owner-nudge-text">
              Who&rsquo;s accountable for delivering this? For now it defaults to whoever created the
              board. Name the project manager — or confirm it&rsquo;s you.
            </p>
            <button
              type="button"
              {...inert('btn btn-small plan2-owner-nudge-btn', () => {
                openAboutEdit();
                setNudgeToPM(true);
              })}
            >
              Name the project manager
            </button>
          </div>
        )}

        {(hasAbout || mayPlan) && (
          <div id="plan-about" className={`plan2-about ${aboutOpen ? 'is-open' : ''}`}>
            <button
              type="button"
              className="plan2-about-toggle"
              aria-expanded={aboutOpen}
              onClick={() => setAboutOpen((v) => !v)}
            >
              <span className="plan2-about-chev">
                <Icon name="chevron" size={18} />
              </span>
              <span className="plan2-about-title">Project details</span>
              {!aboutOpen &&
                (aboutPeek ? (
                  <span className="plan2-about-peek">{aboutPeek}</span>
                ) : (
                  mayPlan && <span className="plan2-about-peek muted">Add the vendor and project detail</span>
                ))}
            </button>

            {aboutOpen &&
              (aboutEdit ? (
                <form className="plan2-about-form" onSubmit={(e) => e.preventDefault()}>
                  {/* OVERVIEW — what the project is and how it will be judged.
                      A rich-text field is NOT a single labelable control: it is a
                      toolbar of buttons plus an editable surface. Wrapping it in a
                      <label> makes the browser forward any click in the field to the
                      label's first labelable descendant — the H₂ button — which
                      silently turned the first line into a heading the moment you
                      clicked in to type. So these use a plain div.stack; the editor
                      carries its own aria-label. (Plain inputs below stay labels.) */}
                  <section className="fd-sec">
                    <h3 className="fd-sec-h">Overview</h3>
                    <div className="stack">
                      <span>What we&rsquo;re undertaking</span>
                      <RichTextEditor
                        value={aDesc}
                        onChange={setADesc}
                        ariaLabel="What we're undertaking"
                        placeholder="What this project sets out to do."
                      />
                    </div>

                    <div className="stack">
                      <span>Success measures / expected outcomes</span>
                      <RichTextEditor
                        value={aSuccess}
                        onChange={setASuccess}
                        ariaLabel="Success measures / expected outcomes"
                        placeholder="What good looks like — the outcome this delivers, in terms a sponsor would recognise."
                      />
                    </div>

                    <label className="stack">
                      <span>Priority</span>
                      <select
                        className="input"
                        value={aPriority}
                        onChange={(e) => setAPriority(e.target.value as ProjectPriority | '')}
                      >
                        <option value="">No priority set</option>
                        {PROJECT_PRIORITIES.map((p) => (
                          <option key={p.value} value={p.value}>
                            {p.label}
                          </option>
                        ))}
                      </select>
                    </label>
                  </section>

                  {/* PEOPLE — who runs it, who owns it, who may authorise its payments. */}
                  <section className="fd-sec">
                    <h3 className="fd-sec-h">People</h3>
                    <div className="fd-grid">
                      <label className="stack" id="plan-pm-field">
                        <span>Project Manager</span>
                        <input
                          className="input"
                          value={aPM}
                          onChange={(e) => setAPM(e.target.value)}
                          placeholder="Who is accountable for delivery"
                        />
                      </label>
                      <label className="stack">
                        <span>Sponsor</span>
                        <input
                          className="input"
                          value={aSponsor}
                          onChange={(e) => setASponsor(e.target.value)}
                          placeholder="Who owns it on the client side"
                        />
                      </label>
                    </div>

                  {/* PAYMENT APPROVALS (#invoice-governance) — who may approve a
                      payment line on this project. Role-first display (the seat
                      survives staff turnover); recommended, not required, so no
                      asterisk — unset, approvals fall back to an admin or manager.
                      A real person picker (not free text) so eligibility is real. */}
                  <div className="plan2-payappr" data-reveal="pay-approvers">
                    <div className="plan2-payappr-h">
                      Payment approvals
                      <span className="plan2-payappr-sub">Who may approve payments on this project</span>
                    </div>
                    {approverPicking ? (
                      <ApproverPicker
                        current={aPayApprover}
                        onPick={(email) => { setAPayApprover(email); setApproverPicking(false); }}
                        onCancel={() => setApproverPicking(false)}
                      />
                    ) : aPayApprover ? (
                      <div className="payappr-set">
                        <span className="payappr-ic"><Icon name="shield" size={18} /></span>
                        <span className="payappr-who">
                          <b>{profiles[aPayApprover.toLowerCase()]?.title?.trim() || displayName(aPayApprover, profiles)}</b>
                          {profiles[aPayApprover.toLowerCase()]?.title?.trim() && (
                            <span className="payappr-name">Currently {displayName(aPayApprover, profiles)}</span>
                          )}
                        </span>
                        <button className="btn btn-small" onClick={() => setApproverPicking(true)}>
                          <Icon name="pencil" size={13} /> Change
                        </button>
                      </div>
                    ) : (
                      <div className="payappr-unset">
                        <span className="payappr-ic"><Icon name="info" size={16} /></span>
                        <span>Not set — approvals fall back to an admin or manager.</span>
                        <button className="btn btn-small" onClick={() => setApproverPicking(true)}>Set an approver</button>
                      </div>
                    )}
                    <p className="payappr-note">
                      Approves payments once the supplier invoice is attached. Must be a board manager,
                      owner or workspace admin. When they are away, their <b>When I’m away</b> cover approves
                      in their place; a single line can route to a different approver on the line itself.
                    </p>
                  </div>
                  </section>

                  {/* COMMERCIAL — who delivers it, the contract behind it, and the
                      stakeholders it answers to. */}
                  <section className="fd-sec">
                    <h3 className="fd-sec-h">Commercial</h3>
                    <div className="fd-grid">
                      <label className="stack">
                        <span>Vendor</span>
                        <input
                          className="input"
                          value={aVendor}
                          onChange={(e) => setAVendor(e.target.value)}
                          placeholder="Who is delivering it"
                        />
                      </label>
                      <label className="stack">
                        <span>Vendor contact</span>
                        <input
                          className="input"
                          value={aVendorContact}
                          onChange={(e) => setAVendorContact(e.target.value)}
                          placeholder="Name or email at the vendor"
                        />
                      </label>
                    </div>
                    <label className="stack">
                      <span>Contract / SOW reference</span>
                      <input
                        className="input"
                        value={aContract}
                        onChange={(e) => setAContract(e.target.value)}
                        placeholder="Contract or statement-of-work number or link"
                      />
                    </label>

                    <div className="stack">
                    <span>Stakeholders</span>
                    <ul className="plan2-stake-edit">
                      {aStake.map((s, i) => (
                        <li key={i}>
                          <input
                            className="input"
                            value={s.name}
                            onChange={(e) =>
                              setAStake((xs) => xs.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)))
                            }
                            placeholder="Name"
                          />
                          <input
                            className="input"
                            value={s.role ?? ''}
                            onChange={(e) =>
                              setAStake((xs) => xs.map((x, j) => (j === i ? { ...x, role: e.target.value } : x)))
                            }
                            placeholder="Role"
                          />
                          <button
                            type="button"
                            className="plan2-stake-x"
                            aria-label={`Remove ${s.name || 'this stakeholder'}`}
                            onClick={() => setAStake((xs) => xs.filter((_, j) => j !== i))}
                          >
                            <Icon name="close" size={13} />
                          </button>
                        </li>
                      ))}
                    </ul>
                    <button
                      type="button"
                      className="plan2-stake-add"
                      onClick={() => setAStake((xs) => [...xs, { name: '', role: '' }])}
                    >
                      + Add stakeholder
                    </button>
                    </div>
                  </section>

                  {/* SCOPE — real rich-text fields now (0113): they feed the
                      requirements document's Scope section directly, so they live
                      here and stay editable rather than being pushed into a document
                      you then cannot change. */}
                  <section className="fd-sec">
                    <h3 className="fd-sec-h">Scope</h3>
                    <p className="plan2-about-group-sub">
                      What this project will and won&rsquo;t deliver — it fills the requirements document&rsquo;s Scope section.
                    </p>
                    <div className="fd-grid">
                      <div className="stack">
                        <span>In scope</span>
                        <RichTextEditor
                          value={aScopeIn}
                          onChange={setAScopeIn}
                          ariaLabel="In scope"
                          placeholder="What this project will deliver."
                        />
                      </div>
                      <div className="stack">
                        <span>Out of scope</span>
                        <RichTextEditor
                          value={aScopeOut}
                          onChange={setAScopeOut}
                          ariaLabel="Out of scope"
                          placeholder="What it deliberately won't cover."
                        />
                      </div>
                    </div>
                  </section>

                  <section className="fd-sec">
                    <h3 className="fd-sec-h">Assumptions &amp; Constraints</h3>
                    <p className="plan2-about-group-sub">
                      What we&rsquo;re taking as given, and the limits we work inside.
                    </p>
                    <div className="fd-grid">
                      <div className="stack">
                        <span>Assumptions</span>
                        <RichTextEditor
                          value={aAssumptions}
                          onChange={setAAssumptions}
                          ariaLabel="Assumptions"
                          placeholder="What we're taking as given."
                        />
                      </div>
                      <div className="stack">
                        <span>Constraints</span>
                        <RichTextEditor
                          value={aConstraints}
                          onChange={setAConstraints}
                          ariaLabel="Constraints"
                          placeholder="The limits we have to work inside."
                        />
                      </div>
                    </div>
                  </section>

                  {/* Milestones, risks, RACI and the sign-off narrative are the
                      fuller story — still the charter document's, not structured
                      fields, so a pointer to it remains. */}
                  {charterDoc ? (
                    <div className="plan2-charter-doc">
                      <span className="plan2-charter-doc-ic">
                        <Icon name="document" size={20} />
                      </span>
                      <div className="plan2-charter-doc-main">
                        <span className="plan2-charter-doc-title">
                          Milestones, risks, RACI and the sign-off narrative
                        </span>
                        <span className="plan2-charter-doc-sub">
                          The fuller story lives in the project charter document.
                        </span>
                      </div>
                      <button type="button" className="btn btn-small" onClick={openCharter}>
                        Open the charter
                      </button>
                    </div>
                  ) : (
                    <div className="plan2-charter-doc">
                      <span className="plan2-charter-doc-ic">
                        <Icon name="document" size={20} />
                      </span>
                      <div className="plan2-charter-doc-main">
                        <span className="plan2-charter-doc-title">Milestones, risks, RACI and the sign-off narrative</span>
                        <span className="plan2-charter-doc-sub">
                          The fuller story is written in a charter document. Save this, then start one.
                        </span>
                      </div>
                    </div>
                  )}

                  <div className="plan2-about-actions">
                    <span className="plan2-about-save" role="status">
                      {aboutStatus === 'saving' ? 'Saving…' : aboutStatus === 'saved' ? 'Saved' : ''}
                    </span>
                    <button type="button" className="btn btn-small" onClick={closeAbout}>
                      Done
                    </button>
                  </div>
                </form>
              ) : (
                <div className="plan2-about-body">
                  {hasAbout ? (
                    <div className="plan2-charter">
                      {/* OVERVIEW — the two narratives, each a section of the document. */}
                      {(richHasText(project?.description) || richHasText(project?.successMeasure)) && (
                        <section className="fd-sec">
                          <h3 className="fd-sec-h">Overview</h3>
                          <dl className="plan2-charter-dl">
                            {richHasText(project?.description) && (
                              <div className="plan2-charter-block is-narrative">
                                <dt>What we&rsquo;re undertaking</dt>
                                <dd className="plan2-charter-rich">
                                  <RichTextEditor value={project!.description!} editable={false} />
                                </dd>
                              </div>
                            )}
                            {richHasText(project?.successMeasure) && (
                              <div className="plan2-charter-block is-narrative">
                                <dt>Success measures / expected outcomes</dt>
                                <dd className="plan2-charter-rich">
                                  <RichTextEditor value={project!.successMeasure!} editable={false} />
                                </dd>
                              </div>
                            )}
                          </dl>
                        </section>
                      )}

                      {/* PEOPLE — who runs it, who owns it. */}
                      {(project?.projectManager?.trim() || project?.sponsor?.trim()) && (
                        <section className="fd-sec">
                          <h3 className="fd-sec-h">People</h3>
                          <dl className="plan2-charter-dl">
                            <div className="plan2-charter-pair">
                              {project?.projectManager?.trim() && (
                                <div className="plan2-charter-cell">
                                  <dt>Project Manager</dt>
                                  <dd>{project.projectManager}</dd>
                                </div>
                              )}
                              {project?.sponsor?.trim() && (
                                <div className="plan2-charter-cell">
                                  <dt>Sponsor</dt>
                                  <dd>{project.sponsor}</dd>
                                </div>
                              )}
                            </div>
                          </dl>
                        </section>
                      )}

                      {/* COMMERCIAL — vendor, the contract behind it, and stakeholders. */}
                      {(project?.vendor?.trim() ||
                        project?.contractRef?.trim() ||
                        project?.vendorContact?.trim() ||
                        aboutStake.length > 0) && (
                        <section className="fd-sec">
                          <h3 className="fd-sec-h">Commercial</h3>
                          <dl className="plan2-charter-dl">
                            {(project?.vendor?.trim() ||
                              project?.contractRef?.trim() ||
                              project?.vendorContact?.trim()) && (
                              <div className="plan2-charter-pair">
                                {project?.vendor?.trim() && (
                                  <div className="plan2-charter-cell">
                                    <dt>Vendor</dt>
                                    <dd>{project.vendor}</dd>
                                  </div>
                                )}
                                {project?.vendorContact?.trim() && (
                                  <div className="plan2-charter-cell">
                                    <dt>Vendor contact</dt>
                                    <dd>{project.vendorContact}</dd>
                                  </div>
                                )}
                                {project?.contractRef?.trim() && (
                                  <div className="plan2-charter-cell">
                                    <dt>Contract / statement of work (SOW)</dt>
                                    <dd>{project.contractRef}</dd>
                                  </div>
                                )}
                              </div>
                            )}
                            {aboutStake.length > 0 && (
                              <div className="plan2-charter-block">
                                <dt>Stakeholders</dt>
                                <dd>
                                  <ul className="plan2-stake">
                                    {aboutStake.map((s, i) => (
                                      <li key={i}>
                                        <span className="plan2-stake-name">{s.name}</span>
                                        {s.role?.trim() && <span className="plan2-stake-role">{s.role}</span>}
                                      </li>
                                    ))}
                                  </ul>
                                </dd>
                              </div>
                            )}
                          </dl>
                        </section>
                      )}

                      {/* SCOPE — in and out, each a full-width section of the document. */}
                      {(richHasText(project?.scopeIn) || richHasText(project?.scopeOut)) && (
                        <section className="fd-sec">
                          <h3 className="fd-sec-h">Scope</h3>
                          <dl className="plan2-charter-dl">
                            {richHasText(project?.scopeIn) && (
                              <div className="plan2-charter-block is-narrative">
                                <dt>In scope</dt>
                                <dd className="plan2-charter-rich">
                                  <RichTextEditor value={project!.scopeIn!} editable={false} />
                                </dd>
                              </div>
                            )}
                            {richHasText(project?.scopeOut) && (
                              <div className="plan2-charter-block is-narrative">
                                <dt>Out of scope</dt>
                                <dd className="plan2-charter-rich">
                                  <RichTextEditor value={project!.scopeOut!} editable={false} />
                                </dd>
                              </div>
                            )}
                          </dl>
                        </section>
                      )}

                      {/* ASSUMPTIONS & CONSTRAINTS — full-width narrative sections. */}
                      {(richHasText(project?.assumptions) || richHasText(project?.constraints)) && (
                        <section className="fd-sec">
                          <h3 className="fd-sec-h">Assumptions &amp; Constraints</h3>
                          <dl className="plan2-charter-dl">
                            {richHasText(project?.assumptions) && (
                              <div className="plan2-charter-block is-narrative">
                                <dt>Assumptions</dt>
                                <dd className="plan2-charter-rich">
                                  <RichTextEditor value={project!.assumptions!} editable={false} />
                                </dd>
                              </div>
                            )}
                            {richHasText(project?.constraints) && (
                              <div className="plan2-charter-block is-narrative">
                                <dt>Constraints</dt>
                                <dd className="plan2-charter-rich">
                                  <RichTextEditor value={project!.constraints!} editable={false} />
                                </dd>
                              </div>
                            )}
                          </dl>
                        </section>
                      )}
                    </div>
                  ) : (
                    <p className="plan2-about-empty">
                      No project detail yet — add who runs it, the vendor and the stakeholders.
                    </p>
                  )}

                  {/* THE CHARTER POINTER — managers only (they alone create it),
                      the same rank the database enforces. Opens the charter with
                      its sign-off state, or starts one when there is none yet. */}
                  {mayPlan &&
                    (charterDoc ? (
                      <div className="plan2-charter-doc">
                        <span className="plan2-charter-doc-ic">
                          <Icon name="document" size={20} />
                        </span>
                        <div className="plan2-charter-doc-main">
                          <span className="plan2-charter-doc-title">Project charter</span>
                          <span className="plan2-charter-doc-sub">
                            Scope, milestones, risk register, RACI and sign-off.
                          </span>
                        </div>
                        <span className={`doc-state is-${charterDoc.state}`}>
                          {DOCUMENT_STATE[charterDoc.state]}
                        </span>
                        <button type="button" className="btn btn-small" onClick={openCharter}>
                          Open the charter
                        </button>
                      </div>
                    ) : (
                      <div className="plan2-charter-doc">
                        <span className="plan2-charter-doc-ic">
                          <Icon name="document" size={20} />
                        </span>
                        <div className="plan2-charter-doc-main">
                          <span className="plan2-charter-doc-title">No project charter yet</span>
                          <span className="plan2-charter-doc-sub">
                            The scope, milestones, risk register, RACI and sign-off are written in a
                            charter document.
                          </span>
                        </div>
                        <button
                          type="button"
                          {...inert('btn btn-small btn-primary', startCharter)}
                        >
                          Start a charter
                        </button>
                      </div>
                    ))}
                  {aboutProblem && <p className="plan2-about-empty">{aboutProblem}</p>}
                  {mayPlan && (
                    <button type="button" {...inert('btn btn-small plan2-about-edit', openAboutEdit)}>
                      {hasAbout ? 'Edit' : 'Add project detail'}
                    </button>
                  )}
                </div>
              ))}
          </div>
        )}

        {live.length === 0 ? (
          <p className="plan2-empty">
            This project has no phases yet.{' '}
            {mayPlan
              ? 'Add the first stage below — or they seed themselves from the project type when a board is made.'
              : 'They are seeded from the project type when a board is made.'}
          </p>
        ) : (
          <ul className="plan2-list">
            {live.map((p, i) => (
              <PhaseRow
                key={p.id}
                phase={p}
                index={i}
                total={live.length}
                prevEnd={i === 0 ? startDay : live[i - 1].targetEnd ?? null}
                goLiveDay={goLiveDay}
                planAnchored={planAnchored}
                cards={cardsByPhase.get(p.id) ?? []}
                allIssues={issues}
                cardsForProgress={cardsForProgress}
                profiles={profiles}
                mayPlan={mayPlan}
                mayAddCards={mayAddCards}
              />
            ))}
          </ul>
        )}

        {mayPlan && <AddPhase />}

        {/* THE MILESTONE REGISTER — dated points and payment stages, below the
            phases spine (milestone payments, approved mocks 9 Oct). Project mode
            only, like the budget: a continuous service has no bounded plan to
            reconcile against a contract. */}
        {project && isProjectMode(project) && (
          <MilestonesCard goLiveDay={goLiveDay} mayPlan={mayPlan} />
        )}

        {/* THE PROJECT'S DOCUMENTS, ON ITS PLAN — a NARROW summary, below the
            phases (the timeline is the Plan's headline; documents support it).
            What the project was handed and its charter list here in full; what it
            has SENT to be signed is one summary line, not a dump — the whole
            record lives on the Documents screen, which each row and the summary
            line drill into. The "← Back to Plan" trail makes that a step down
            from here, not a jump to a shared screen. */}
        <PlanDocuments />
      </div>

      {retired.length > 0 && mayPlan && <RetiredPhases retired={retired} />}

      <p className="plan-foot">
        The phases start from the project type — a different kind of project shows its own stages.
        Everything on this screen is editable, anytime; nothing is locked in.
      </p>
    </div>
  );
}

/**
 * THE PROJECT'S DOCUMENTS, ON ITS PLAN — Slice 4, narrowed.
 *
 * A NARROW summary, not the whole record. The Plan carries what the project IS
 * and WAS HANDED — its received/reference files and the charter (isProjectArtifact)
 * — because those belong beside the plan of work. What the project SENDS OUT to be
 * signed is a delivery concern, so it is one summary line here ("Sent for sign-off
 * — N"), not a dump that buried the phases. The full list, and the whole
 * upload / preview / send / sign workflow, lives on the Documents screen; every
 * row and the summary line drill into it, and each row reads exactly as it does
 * there (shared DocFace) so the two can never drift apart again.
 */
function PlanDocuments() {
  const documents = useApp((s) => s.documents);
  const sprints = useApp((s) => s.sprints);
  const drillTo = useApp((s) => s.drillTo);
  const role = useApp((s) => s.shownRole);
  const mayDraft = can(role, 'editIssue');
  const inert = useSampleInert();

  const sprintName = useMemo(() => {
    const by = new Map(sprints.map((s) => [s.id, s.name]));
    return (id: string) => by.get(id);
  }, [sprints]);

  const artifacts = documents.filter((d) => isProjectArtifact(d.kind));
  const forSignoff = documents.filter((d) => !isProjectArtifact(d.kind));
  const waiting = forSignoff.filter((d) => d.state === 'awaiting').length;

  const row = (d: Doc) => (
    <div key={d.id} className={`doc-row is-${d.state}`}>
      <button type="button" className="doc-line" onClick={() => drillTo('documents', d.id)}>
        <DocFace doc={d} covers={covers(d, sprintName)} />
      </button>
    </div>
  );

  return (
    <section className="plan2-docs">
      <div className="plan2-docs-head">
        <h3 className="plan2-docs-title">Documents</h3>
        {mayDraft && (
          <button
            type="button"
            {...inert('btn btn-small plan2-docs-add', () => drillTo('documents', NEW_DOC))}
          >
            New document
          </button>
        )}
      </div>

      {documents.length === 0 ? (
        <p className="plan2-docs-empty">
          Nothing kept here yet — a statement of work or plan the project was handed, and the charter,
          change requests and deliverables it sends to be signed, all live here.
        </p>
      ) : (
        <>
          {artifacts.length > 0 ? (
            <div className="doc-list">{artifacts.map(row)}</div>
          ) : (
            <p className="plan2-docs-empty">
              Nothing the project was handed yet — a statement of work, a plan or the charter would sit
              here.
            </p>
          )}

          {/* SENT FOR SIGN-OFF, AS A LINE NOT A LIST. The delivery documents belong
              on the Documents screen; here they are one figure that opens it. */}
          {forSignoff.length > 0 && (
            <button
              type="button"
              className="plan2-docs-more"
              onClick={() => drillTo('documents')}
            >
              <span className="plan2-docs-more-text">
                Sent for sign-off — {forSignoff.length}
                {waiting > 0 && ` · ${waiting} awaiting`}
              </span>
              <span className="plan2-docs-more-go" aria-hidden="true">
                →
              </span>
            </button>
          )}
        </>
      )}
    </section>
  );
}

function PhaseRow({
  phase,
  index,
  total,
  prevEnd,
  goLiveDay,
  planAnchored,
  cards,
  allIssues,
  cardsForProgress,
  profiles,
  mayPlan,
  mayAddCards,
}: {
  phase: Phase;
  index: number;
  total: number;
  prevEnd: string | null;
  goLiveDay?: string;
  /** The go-live is anchored to the plan (0217), so a phase ending after it is calm post-launch
   *  work, not a breach — goLiveState reads it as `post-go-live`. */
  planAnchored?: boolean;
  cards: Issue[];
  allIssues: Issue[];
  /** allIssues with each card's EFFECTIVE % stamped in, so the phase bar reflects
   *  checklist-derived progress too. */
  cardsForProgress: Issue[];
  profiles: import('../people').Profiles;
  mayPlan: boolean;
  mayAddCards: boolean;
}) {
  const inert = useSampleInert();
  // The workspace working calendar (0216) — the phase's schedule read counts working
  // days, so a workspace close doesn't make an on-track phase read behind.
  const workCalendar = useApp((s) => s.workCalendar);
  // D1: the status a phase EARNS from its cards, unless a manager has overridden
  // it. A stored 'planned' is the creation default and means "defer to the
  // cards", so it never reads as an override — only a stored 'active' or 'done'
  // that disagrees with the cards is a real, deliberate override (force-started,
  // held open for a gate, or flagged done early), and it is shown as one so it
  // cannot silently drift from what the board says.
  const derived = phaseDerivedStatus(allIssues, phase.id);
  const isOverride = phase.status !== 'planned' && phase.status !== derived;
  const shown: PhaseStatus = isOverride ? phase.status : derived;
  const counts = phaseCardCounts(allIssues, phase.id);
  const pct = phaseProgressFromCards(cardsForProgress, phase.id);
  const behind = shown === 'active' ? Math.max(0, daysPast(phase.targetEnd)) : 0;
  // A plain-words schedule read beside the phase window: how far the work is versus
  // how far through the phase's dates we are. Silent (kind 'none') when there is no
  // window to judge against, so an undated or on-schedule phase stays calm.
  const phaseCards = cards;
  const allResolvedInPhase = phaseCards.length > 0 && phaseCards.every((c) => c.status === 'done' || c.status === 'wontdo');
  const sched = scheduleHealth(pct, expectedPctOfWindow(phase.startsOn, phase.targetEnd, undefined, workCalendar), allResolvedInPhase);
  // Where this phase stands against the project's go-live — the derived answer,
  // read from the status the row actually shows. `past-open` is the red alarm;
  // `planned-breach`/`approaching` are amber warnings; `slipped` is a settled
  // record on a done phase (it finished after the date).
  const gl = goLiveState(phase, goLiveDay, shown, Date.now(), planAnchored);
  const glOver =
    gl === 'planned-breach' && phase.targetEnd
      ? (() => {
          const tMs = dayToMs(phase.targetEnd);
          return tMs ? daysPast(goLiveDay!, tMs) : 0;
        })()
      : 0;

  // Phases start COLLAPSED every time the Plan is entered (Vinnie, 1 Nov) — the view
  // unmounts on navigation and remounts on return (App.tsx `key={view}`), so this
  // default is what a reader meets each time they come back: a calm, folded list they
  // open as they need, never the active phase silently re-expanded under them.
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(false);

  return (
    <li className={`plan2-ph ${open ? 'is-open' : ''}`}>
      <div className="plan2-head">
        <button
          className="plan2-caret"
          aria-expanded={open}
          aria-label={open ? `Collapse ${phase.name}` : `Expand ${phase.name}`}
          onClick={() => setOpen((o) => !o)}
        >
          <Icon name="chevron" size={18} />
        </button>
        <button className="plan2-name" onClick={() => setOpen((o) => !o)}>
          {phase.name}
        </button>
        <span className={`plan2-chip is-${shown}`}>{PHASE_STATUS_LABELS[shown]}</span>
        {isOverride && (
          <span
            className="plan2-ovr"
            title={`Set by hand — the cards read “${PHASE_STATUS_LABELS[derived]}”`}
          >
            override
          </span>
        )}
        {/* When a done phase ACTUALLY finished, and by how much it beat or missed
            its target — reading the done_at the database stamps (0097) against
            the planned targetEnd. A late finish reads amber, an early one green;
            with no target set, just the date. */}
        {shown === 'done' &&
          phase.doneAt != null &&
          (() => {
            const slip = daysPast(phase.targetEnd, phase.doneAt); // + late, − early
            const when = new Date(phase.doneAt).toLocaleDateString('en-GB', {
              day: 'numeric',
              month: 'short',
            });
            const late = phase.targetEnd ? slip > 0 : false;
            const tail = !phase.targetEnd
              ? ''
              : slip > 0
                ? ` · ${slip}d late`
                : slip < 0
                  ? ` · ${-slip}d early`
                  : ' · on time';
            return (
              <span
                className={`plan2-actual ${late ? 'late' : 'early'}`}
                title={`Finished ${when}${phase.targetEnd ? `, target ${shortDay(phase.targetEnd)}` : ''}`}
              >
                <Icon name="check" size={11} />
                finished {when}
                {tail}
              </span>
            );
          })()}
        {/* GO-LIVE — the warning and the indicator that this phase is colliding
            with the project's promised date. Red only for past-and-open; amber
            for a plan that misses it or a go-live coming up; a settled marker when
            a done phase actually finished after the date. */}
        {gl === 'past-open' && (
          <span
            className="plan2-chip is-golive-past"
            title="Unfinished, and the Go-Live date has already passed"
          >
            <Icon name="clock" size={11} />
            Past Go-Live · {daysPast(goLiveDay)}d
          </span>
        )}
        {gl === 'planned-breach' && (
          <span
            className="plan2-chip is-golive-breach"
            title="This phase is planned to end after the Go-Live date"
          >
            <Icon name="clock" size={11} />
            {glOver}d after Go-Live
          </span>
        )}
        {gl === 'approaching' && (
          <span
            className="plan2-chip is-golive-soon"
            title={`The Go-Live is within ${GO_LIVE_APPROACHING_DAYS} days and this phase isn't done`}
          >
            <Icon name="clock" size={11} />
            Due near Go-Live
          </span>
        )}
        {gl === 'slipped' && (
          <span className="plan2-actual late" title="This phase finished after the Go-Live date">
            <Icon name="flag" size={11} />
            after Go-Live
          </span>
        )}
        {/* POST GO-LIVE — the go-live is anchored to the plan and this phase is planned to finish
            AFTER it, which is hypercare / post-launch work by design, not a breach (0217). Calm,
            neutral tone — it informs, it does not alarm. */}
        {gl === 'post-go-live' && (
          <span className="plan2-chip is-postgolive" title="Planned to run after the Go-Live — post-launch work, not a slip">
            <Icon name="clock" size={11} />
            Post go-live
          </span>
        )}
        {/* The plain per-target slip — suppressed when a louder go-live chip already
            speaks for the same phase, so the row never carries two risk chips. */}
        {behind > 0 && gl !== 'past-open' && gl !== 'planned-breach' && gl !== 'post-go-live' && (
          <span className="plan2-chip is-risk">At risk</span>
        )}
        {/* The soft schedule read — shown only when it carries an off-schedule
            signal (behind / ahead / past its window). An on-schedule or finished
            phase stays silent, so a row never carries a redundant second chip. Sits
            after the status chips, in the left cluster, before the window dates. */}
        {(sched.kind === 'behind' || sched.kind === 'ahead' || sched.kind === 'past-window') && (
          <span className={`plan2-sched is-${sched.tone === 'good' ? 'good' : sched.tone === 'bad' ? 'bad' : 'warn'}`}>
            {sched.label}
          </span>
        )}
        <span className="plan2-when">{windowStr(prevEnd, phase.targetEnd)}</span>
        <span className="plan2-prog">
          <span className="plan2-pbar">
            <i className={`is-${shown}`} style={{ width: `${pct}%` }} />
          </span>
          <span className="plan2-count">
            {counts.done} / {counts.total}
          </span>
        </span>
        {mayPlan && (
          <button
            aria-expanded={editing}
            {...inert('btn btn-small plan2-edit', () => setEditing((e) => !e))}
          >
            {editing ? 'Done' : 'Edit'}
          </button>
        )}
      </div>

      {editing && mayPlan && (
        <PhaseEditor phase={phase} index={index} total={total} derived={derived} isOverride={isOverride} />
      )}

      <Expander open={open}>
        <div className="plan2-body">
          {cards.length > 0 ? (
            cards.map((c) => <CardRow key={c.id} card={c} profiles={profiles} />)
          ) : (
            <p className="plan2-none">
              No cards in this phase yet
              {mayAddCards ? ' — add one below, or set a card’s phase on the board.' : '.'}
            </p>
          )}
          {mayAddCards && <AddCard phaseId={phase.id} />}
        </div>
      </Expander>
    </li>
  );
}

function CardRow({ card, profiles }: { card: Issue; profiles: import('../people').Profiles }) {
  const sample = useSampleBoard();
  const openIssue = useApp((s) => s.openIssue);
  const updateIssue = useApp((s) => s.updateIssue);
  const dismissMoscowNudge = useApp((s) => s.dismissMoscowNudge);
  const nudged = useApp((s) => s.moscowNudge.includes(card.id));
  const done = card.status === 'done' || card.status === 'wontdo';
  const who = card.assignee ? initials(card.assignee, profiles) : '';
  const row = (
    <button type="button" className={`plan2-task ${done ? 'is-done' : ''}`} onClick={() => openIssue(card.id)}>
      <span className={`plan2-dot s-${card.status}`} aria-hidden="true" />
      <span className="plan2-cref">{card.ref}</span>
      <span className="plan2-tn">{card.title}</span>
      <span className="plan2-tmeta">
        <span className="plan2-tstatus">{STATUS_LABELS[card.status]}</span>
        {who && (
          <span className="plan2-who" title={displayName(card.assignee ?? '', profiles)}>
            <VendorTag who={card.assignee ?? ''} profiles={profiles} glyphOnly />
            {who}
          </span>
        )}
      </span>
    </button>
  );
  // Only nudge a card that was just placed here AND still has no level — the
  // button can't nest the nudge's buttons, so the strip sits below it.
  if (!nudged || card.moscow) return row;
  return (
    <div className="plan2-tasknudge">
      {row}
      <MoscowNudge
        onPick={(level) => {
          if (sample.readOnly) { void sample.note(); return; }
          updateIssue(card.id, { moscow: level });
        }}
        onDismiss={() => {
          if (sample.readOnly) { void sample.note(); return; }
          dismissMoscowNudge(card.id);
        }}
      />
    </div>
  );
}

/**
 * Add a card to a phase — find an existing one, or make a new one. Typing
 * narrows the board's cards by number or title (the ones not already in this
 * phase); picking one PULLS it in (sets its phase), and the last row always
 * offers to CREATE a new card with what you typed, for when nothing matches.
 * The plan is a view over the cards, so pulling a card in is the ordinary way a
 * stage fills — Vinnie, projectization.
 */
function AddCard({ phaseId }: { phaseId: string }) {
  const inert = useSampleInert();
  const createIssue = useApp((s) => s.createIssue);
  const updateIssue = useApp((s) => s.updateIssue);
  const issues = useApp((s) => s.issues);
  const phases = useApp((s) => s.phases);
  const issueTypes = useApp((s) => s.issueTypes);
  const meta = useApp((s) => s.meta);
  const access = useApp((s) => s.access);
  const profiles = useApp((s) => s.profiles);
  const projects = useApp((s) => s.projects);
  const projectId = useApp((s) => s.projectId);
  const groupsOf = useApp((s) => s.groupsOf);
  const meEmail = useApp((s) => s.user?.email ?? '');
  const [adding, setAdding] = useState(false);
  const [text, setText] = useState('');
  const [hi, setHi] = useState(0);
  const [busy, setBusy] = useState(false);

  const kind = liveTypes(issueTypes)[0]?.slug ?? 'task';
  // A card added from the plan pre-fills both assignee slots from the creator's
  // resolved stance (company role + board/person override, src/company-role.ts), so
  // it is owned on landing — a manager Accountable, a doer Responsible, the sole
  // assistant as the doer on an assistant-run board. Still editable afterwards.
  const meProfile = profiles[meEmail] ?? profiles[meEmail.toLowerCase()];
  const meDefault = cardCreateDefault({
    me: meEmail,
    personStance: meProfile?.cardDefaultStance,
    companyRole: meProfile?.companyRole,
    boardDefault: meta.cardDefaults,
    soleAssistant: defaultAssignee(meta),
  });
  const phaseName = (id?: string | null) => phases.find((p) => p.id === id)?.name ?? '';

  /*
   * QUICK-ADD TOKENS WORK HERE TOO — Vinnie, 11 Sep: *"when adding cards from the
   * project plan, quick add features should be available … the only feature that
   * may not be needed is the phase, as it is being added within the phase."* So a
   * new card typed here takes the same *type @assignee !priority ~size tokens as
   * the board's bar, resolved against the SAME people (boardAssignees, so the
   * choices match the card and the bar), but with `noPhase`: the phase is fixed
   * to this one, and `#132` stays a way to find a card by ref rather than a phase.
   */
  const world = useMemo(() => {
    const onBoard = boardAssignees(
      projects.find((p) => p.id === projectId),
      access?.emails ?? [],
      access?.admins ?? [],
      groupsOf
    );
    const persons = [...new Set(onBoard.map((e) => e.toLowerCase()))].map((email) => ({
      email,
      name: displayName(email, profiles),
    }));
    const bots = assistantsOf(meta).map((a) => ({ email: `assistant:${a.id}`, name: a.name?.trim() || a.id }));
    return {
      types: liveTypes(issueTypes).map((t) => ({ slug: t.slug, name: t.name })),
      people: [...persons, ...bots],
      phases: [] as { id: string; name: string }[],
    };
  }, [issueTypes, access, profiles, meta, projects, projectId, groupsOf]);

  /* Parsed with noPhase, so the title is what is left once the tokens are read,
     and the search below narrows on that title rather than on the raw tokens. */
  const parsed = useMemo(() => parseQuickAdd(text, world, { noPhase: true }), [text, world]);

  const q = parsed.title.trim().toLowerCase();
  // The board's cards ranked against what has been typed: an exact ref first,
  // then a ref or title that starts with it, then one that merely contains it.
  // Cards already in this phase are left out — pulling one in again is a no-op.
  const matches = useMemo(() => {
    if (!q) return [];
    const bare = q.replace(/[^a-z0-9]/g, '');
    const scored: { i: Issue; s: number }[] = [];
    for (const i of issues) {
      if (i.phase === phaseId) continue;
      const ref = i.ref.toLowerCase();
      const refBare = ref.replace(/[^a-z0-9]/g, '');
      const title = i.title.toLowerCase();
      let s = -1;
      if (ref === q || (!!bare && refBare === bare)) s = 0;
      else if (ref.startsWith(q) || (!!bare && refBare.startsWith(bare))) s = 1;
      else if (title.startsWith(q)) s = 2;
      else if (title.includes(q) || (!!bare && refBare.includes(bare))) s = 3;
      if (s >= 0) scored.push({ i, s });
    }
    scored.sort((a, b) => a.s - b.s || a.i.title.localeCompare(b.i.title));
    return scored.slice(0, 6).map((x) => x.i);
  }, [q, issues, phaseId]);

  // The rows the arrow keys walk: every match, then the "create" row when there
  // is text to make a card from. The highlight is clamped so it never points off
  // the end when the list shrinks under it.
  const createIndex = matches.length;
  const rowCount = matches.length + (q ? 1 : 0);
  const H = Math.min(hi, Math.max(0, rowCount - 1));

  const pull = (id: string) => {
    updateIssue(id, { phase: phaseId });
    setText('');
    setHi(0);
  };
  const create = async () => {
    const title = parsed.title.trim();
    if (!title || busy) return;
    setBusy(true);
    try {
      await createIssue({
        kind: parsed.kind?.slug ?? kind,
        title,
        description: '',
        status: 'todo' as IssueStatus,
        priority: parsed.priority ?? 'medium',
        assignee: parsed.assignee?.email ?? meDefault.assignee,
        responsible: meDefault.responsible,
        labels: [],
        /* Always this phase — the card is being added within it, so there is no
           #phase token to set it (see the world above). */
        phase: phaseId,
        size: parsed.size ?? undefined,
        area: null,
        dueDate: parsed.due,
      });
      setText('');
      setHi(0);
    } finally {
      setBusy(false);
    }
  };
  const activate = () => {
    if (matches.length && H < matches.length) pull(matches[H].id);
    else void create();
  };

  return adding ? (
    <div className="plan2-addcard">
      <div className="plan2-ac-wrap">
        <input
          className="input"
          autoFocus
          value={text}
          placeholder="Find a card, or type a new one — @who *type !priority ~size ^due"
          onChange={(e) => {
            setText(e.target.value);
            setHi(0);
          }}
          onKeyDown={(e) => {
            if (e.key === 'ArrowDown') {
              e.preventDefault();
              setHi((h) => Math.min(h + 1, rowCount - 1));
            } else if (e.key === 'ArrowUp') {
              e.preventDefault();
              setHi((h) => Math.max(h - 1, 0));
            } else if (e.key === 'Enter') {
              e.preventDefault();
              activate();
            } else if (e.key === 'Escape') {
              setText('');
              setHi(0);
              setAdding(false);
            }
          }}
        />
        {q && (
          <ul className="plan2-ac">
            {matches.map((m, idx) => (
              <li key={m.id}>
                <button
                  type="button"
                  className={`plan2-ac-row ${idx === H ? 'is-hi' : ''}`}
                  onMouseEnter={() => setHi(idx)}
                  onClick={() => pull(m.id)}
                >
                  <span className="plan2-ac-ref">{m.ref}</span>
                  <span className="plan2-ac-title">{m.title}</span>
                  <span className="plan2-ac-meta">{m.phase ? `in ${phaseName(m.phase)}` : 'no phase'}</span>
                </button>
              </li>
            ))}
            <li>
              <button
                type="button"
                className={`plan2-ac-row plan2-ac-create ${H === createIndex ? 'is-hi' : ''}`}
                onMouseEnter={() => setHi(createIndex)}
                onClick={() => void create()}
                disabled={busy}
              >
                + Create a new card &ldquo;{parsed.title.trim()}&rdquo;
              </button>
            </li>
          </ul>
        )}
      </div>
      <button
        className="btn btn-small"
        onClick={() => {
          setAdding(false);
          setText('');
          setHi(0);
        }}
      >
        Done
      </button>
    </div>
  ) : (
    <button {...inert('plan2-ghost', () => setAdding(true))}>
      + Add a card to this phase, or pull one in
    </button>
  );
}

function PhaseEditor({
  phase,
  index,
  total,
  derived,
  isOverride,
}: {
  phase: Phase;
  index: number;
  total: number;
  derived: PhaseStatus;
  isOverride: boolean;
}) {
  const editPhase = useApp((s) => s.editPhase);
  const movePhase = useApp((s) => s.movePhase);
  const retirePhase = useApp((s) => s.retirePhase);
  const [says, setSays] = useState('');
  const run = async (p: Promise<string>) => setSays(await p);

  return (
    <div className="plan2-editor">
      <div className="plan2-fields">
        <RenameField value={phase.name} onRename={(name) => void run(editPhase(phase.id, { name }))} />
        <label className="plan2-field">
          <span>Status</span>
          <select
            className="input"
            value={phase.status}
            onChange={(e) => void run(editPhase(phase.id, { status: e.target.value as PhaseStatus }))}
          >
            {PHASE_STATUSES.map((s) => (
              <option key={s} value={s}>
                {PHASE_STATUS_LABELS[s]}
                {s === derived ? ' — from the cards' : ''}
              </option>
            ))}
          </select>
        </label>
        <label className="plan2-field">
          <span>Target end</span>
          <DateField
            ariaLabel="Target end"
            variant="form"
            value={phase.targetEnd ?? ''}
            onChange={(v) => void run(editPhase(phase.id, { targetEnd: v || null }))}
          />
        </label>
        <div className="plan2-field">
          <span>Order</span>
          <span className="plan2-moves">
            <button
              className="btn btn-small"
              disabled={index === 0}
              aria-label={`Move ${phase.name} up`}
              onClick={() => void run(movePhase(phase.id, -1))}
            >
              ↑
            </button>
            <button
              className="btn btn-small"
              disabled={index === total - 1}
              aria-label={`Move ${phase.name} down`}
              onClick={() => void run(movePhase(phase.id, 1))}
            >
              ↓
            </button>
          </span>
        </div>
      </div>
      <div className="plan2-editor-foot">
        {isOverride && (
          <button className="link" onClick={() => void run(editPhase(phase.id, { status: derived }))}>
            Use the cards’ status ({PHASE_STATUS_LABELS[derived]})
          </button>
        )}
        <button
          className="btn btn-small plan2-remove"
          onClick={async () => {
            // Every comparable destructive control asks first, and this one is a
            // single click that reshapes the plan. Reassure while asking: it is
            // reversible (Retired Phases › Put back) and does not disturb the
            // cards already running in the phase.
            const ok = await confirmDialog({
              title: `Remove the “${phase.name}” phase?`,
              body:
                'It stops being offered on new work; cards already in it stay where they are, and you can put it back from Retired Phases below.',
              confirmLabel: 'Remove',
              tone: 'danger',
            });
            if (!ok) return;
            void run(retirePhase(phase.id));
          }}
        >
          Remove phase
        </button>
      </div>
      {says && (
        <p className="plan2-err" role="alert">
          {says}
        </p>
      )}
    </div>
  );
}

function AddPhase() {
  const inert = useSampleInert();
  const createPhase = useApp((s) => s.createPhase);
  const [adding, setAdding] = useState(false);
  const [name, setName] = useState('');
  const [says, setSays] = useState('');

  const add = async () => {
    const why = await createPhase({ name });
    setSays(why);
    if (!why) {
      setName('');
      setAdding(false);
    }
  };

  return adding ? (
    <div className="plan2-addphase is-adding">
      <input
        className="input"
        autoFocus
        value={name}
        placeholder="Discovery, Build, Cutover…"
        onChange={(e) => {
          setName(e.target.value);
          setSays('');
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            void add();
          }
          if (e.key === 'Escape') {
            setAdding(false);
            setName('');
            setSays('');
          }
        }}
      />
      <button className="btn btn-primary btn-small" disabled={!name.trim()} onClick={() => void add()}>
        Add it
      </button>
      <button
        className="btn btn-small"
        onClick={() => {
          setAdding(false);
          setName('');
          setSays('');
        }}
      >
        Cancel
      </button>
      {says && (
        <span className="plan2-err" role="alert">
          {says}
        </span>
      )}
    </div>
  ) : (
    <div className="plan2-addphase">
      <button data-reveal="add-phase" {...inert('plan2-ghost', () => setAdding(true))}>
        + Add a phase
      </button>
    </div>
  );
}

/* ── The milestone register — #374 + milestone payments (approved mocks). ──── */

const KIND_ICON: Record<MilestoneKind, IconName> = {
  contract: 'link',
  delivery: 'package',
  golive: 'flag',
  signoff: 'task',
  regulatory: 'shield',
  other: 'pin',
};

/** A `YYYY-MM-DD` day as "18 Jun 2026" — the register carries the year, unlike the
 *  phase windows (which read within one plan). */
function milestoneDay(day?: string | null): string {
  if (!day) return '';
  const [y, m, d] = day.split('-').map(Number);
  if (!y || !m || !d) return '';
  return new Date(y, m - 1, d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

/** The billing state as the one RAG word on a billable row — colour is used here
 *  and nowhere else on the row, so status reads at a glance (mock 1a). */
const BILLING_BADGE: Record<'paid' | 'claimed' | 'part-claimed' | 'scheduled', { cls: string; word: string }> = {
  paid: { cls: 'g', word: 'Paid' },
  claimed: { cls: 'a', word: 'Claimed' },
  'part-claimed': { cls: 'a', word: 'Part-claimed' },
  scheduled: { cls: 'n', word: 'Scheduled' },
};

/** The schedule state as the RAG word on a marker that carries no payment (a
 *  go-live, a promoted phase end, a non-billable point) — "from the plan". */
const SCHEDULE_BADGE: Record<MilestoneState, { cls: string; word: string }> = {
  done: { cls: 'g', word: 'Done' },
  late: { cls: 'r', word: 'Late' },
  slipping: { cls: 'a', word: 'Slipping' },
  'on-track': { cls: 'n', word: 'Upcoming' },
};

/* ── The Go-Live anchor editor (0217) — point the go-live at the work that delivers it, so its
 *    date and forecast come from the plan rather than a loose typed date. Three ways to anchor:
 *    the end of a phase (default), a milestone, or a committed fixed date (the labelled fallback).
 *    A live derived-date + forecast preview before you commit. Built to the approved mock. ──── */
function GoLiveAnchorModal({
  project,
  projectId,
  phases,
  milestones,
  issues,
  onClose,
}: {
  project: Project;
  projectId: string;
  phases: Phase[];
  milestones: MilestoneRow[];
  issues: Issue[];
  onClose: () => void;
}) {
  const { leaving, dismiss } = useDismiss(onClose);
  const panelRef = useRef<HTMLDivElement>(null);
  // Land focus IN the dialog on open (and trap Tab), so Escape works on the common
  // mouse-open path — the key is read on the panel's onKeyDown, which only fires once
  // focus is inside (the L112/L139 modal-focus contract, same as the handoff dialog).
  useFocusTrap(panelRef, true);
  const updateProject = useApp((s) => s.updateProject);
  const workCalendar = useApp((s) => s.workCalendar);

  const datedPhases = phases.filter((p) => p.targetEnd);
  const datedMs = milestones.filter((m) => m.targetDay);
  const cur = project.goLiveAnchor;
  const [kind, setKind] = useState<GoLiveAnchor['kind']>(
    cur?.kind ?? (datedPhases.length ? 'phase' : 'fixed'),
  );
  const [phaseId, setPhaseId] = useState<string>(
    (cur?.kind === 'phase' && cur.id) || datedPhases[datedPhases.length - 1]?.id || '',
  );
  const [msId, setMsId] = useState<string>((cur?.kind === 'milestone' && cur.id) || datedMs[0]?.id || '');
  const [fixedDay, setFixedDay] = useState<string>(
    project.goLive ? new Date(project.goLive).toISOString().slice(0, 10) : '',
  );

  const selPhase = datedPhases.find((p) => p.id === phaseId);
  const selMs = datedMs.find((m) => m.id === msId);
  const derivedDay =
    kind === 'phase' ? selPhase?.targetEnd ?? undefined
    : kind === 'milestone' ? selMs?.targetDay ?? undefined
    : fixedDay || undefined;

  // A live forecast preview over the anchored scope — the phase's open cards (default) resample
  // the board's throughput, exactly as the milestone register does, so the editor and the Plan
  // read one number. Only for a phase/milestone anchor; a fixed date is a committed date, not a
  // prediction.
  const preview = useMemo(() => {
    if (kind === 'fixed' || !derivedDay) return null;
    const key = kind === 'phase' ? `phase:${phaseId}` : `ms:${msId}`;
    const synthetic = {
      key, name: 'Go-Live', kind: 'golive', boardId: project.id, projectName: project.name,
      plannedDay: derivedDay, state: 'on-track', slipDays: 0,
      ...(kind === 'milestone' && selMs?.forecastTie ? { forecastTie: selMs.forecastTie } : {}),
    } as unknown as Milestone;
    const scope = milestoneScope(synthetic, issues) ?? [];
    const open = scope.filter((i) => i.status !== 'done' && i.status !== 'wontdo').length;
    const fc = milestoneForecast(synthetic, issues, Date.now(), FORECAST_TRIALS_GLANCE, workCalendar);
    return { open, fc };
  }, [kind, derivedDay, phaseId, msId, selMs, issues, project.id, project.name, workCalendar]);

  // A committed fixed date that is already past: Go-Live is the deadline for the project
  // to be live, so a past one is almost always a slip of the finger — warn, don't block
  // (an overdue project may legitimately record a past commitment). Day-string compare is
  // chronological; both sides are plain UTC days.
  const fixedInPast = kind === 'fixed' && !!fixedDay && fixedDay < new Date().toISOString().slice(0, 10);
  const canCommit = kind === 'fixed' ? !!fixedDay : !!derivedDay;
  const commit = () => {
    if (!canCommit) return;
    if (kind === 'phase' || kind === 'milestone') {
      const anchor: GoLiveAnchor = kind === 'phase' ? { kind: 'phase', id: phaseId } : { kind: 'milestone', id: msId };
      // Freeze the COMMITTED baseline (0217 / pm R1a): a plan anchor derives the live go-live
      // day, but the as-delivered record must measure slip against a date that does NOT move
      // when the phase/milestone moves. Keep an existing committed date; otherwise capture the
      // day being committed to now. `project.goLive` is that frozen baseline from here on.
      const patch: { goLiveAnchor: GoLiveAnchor; goLive?: number } = { goLiveAnchor: anchor };
      if (project.goLive == null && derivedDay) patch.goLive = dayToMs(derivedDay);
      void updateProject(projectId, patch);
    } else {
      void updateProject(projectId, { goLiveAnchor: { kind: 'fixed' }, goLive: dayToMs(fixedDay) });
    }
    dismiss();
  };

  const overBy = (() => {
    if (!preview?.fc?.ready || !derivedDay) return 0;
    return Math.round((preview.fc.p85 - (dayToMs(derivedDay) ?? 0)) / 86_400_000);
  })();

  return (
    <div className={`overlay ${leaving ? 'is-leaving' : ''}`} onClick={dismiss}>
      <div
        ref={panelRef}
        className={`modal golive-anchor-modal ${leaving ? 'is-leaving' : ''}`}
        role="dialog"
        aria-label="Anchor Go-Live"
        // tabIndex so useFocusTrap's root.focus() lands focus IN the dialog on open,
        // so the Escape key below is heard on the common mouse-open path.
        tabIndex={-1}
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => { if (e.key === 'Escape') dismiss(); }}
      >
        <div className="modal-head">
          <span className="modal-ref"><span className="modal-head-title">Anchor Go-Live</span></span>
          <div className="modal-head-actions">
            <button className="modal-close" onClick={dismiss} tabIndex={-1} aria-label="Close (Escape)">
              ESC
            </button>
          </div>
        </div>
        <div className="golive-anchor-body">
          <p className="golive-anchor-lead">
            Point Go-Live at the work that delivers it, so its date and forecast come from the plan — never a date floating on its own.
          </p>
          <div className="seg" role="tablist" aria-label="How to anchor Go-Live">
            {([
              ['phase', 'End of a phase'],
              ['milestone', 'A milestone'],
              ['fixed', 'A fixed date'],
            ] as const).map(([k, label]) => (
              <button
                key={k}
                role="tab"
                aria-selected={kind === k}
                className={`seg-btn ${kind === k ? 'active' : ''}`}
                onClick={() => setKind(k)}
                disabled={(k === 'phase' && !datedPhases.length) || (k === 'milestone' && !datedMs.length)}
                title={
                  k === 'phase' && !datedPhases.length ? 'No phase has a target end to anchor to yet'
                  : k === 'milestone' && !datedMs.length ? 'No dated milestone to anchor to yet'
                  : undefined
                }
              >
                {label}
              </button>
            ))}
          </div>

          {kind === 'phase' && (
            <label className="stack golive-anchor-field">
              <span className="golive-anchor-label">Phase <span className="req" aria-hidden="true">*</span></span>
              <select className="input" value={phaseId} onChange={(e) => setPhaseId(e.target.value)} aria-required="true">
                {datedPhases.map((p) => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
            </label>
          )}
          {kind === 'milestone' && (
            <label className="stack golive-anchor-field">
              <span className="golive-anchor-label">Milestone <span className="req" aria-hidden="true">*</span></span>
              <select className="input" value={msId} onChange={(e) => setMsId(e.target.value)} aria-required="true">
                {datedMs.map((m) => (
                  <option key={m.id} value={m.id}>{m.name}</option>
                ))}
              </select>
            </label>
          )}
          {kind === 'fixed' && (
            <label className="stack golive-anchor-field">
              <span className="golive-anchor-label">Committed date <span className="req" aria-hidden="true">*</span></span>
              <DateField variant="form" ariaLabel="Go-Live date" value={fixedDay} onChange={() => {}} onCommit={(d) => setFixedDay(d)} />
              {fixedInPast && (
                <div className="golive-anchor-warn">
                  This date is already past. Go-Live is the deadline for the project to be live and rolled out, so it is normally a future date — check this is what you mean.
                </div>
              )}
            </label>
          )}

          {derivedDay && (
            <div className="golive-anchor-preview">
              <div>
                Go-Live lands on <b>{shortDay(derivedDay)}</b>
                {kind === 'phase' && selPhase ? <>, the end of <b>{selPhase.name}</b>.</>
                  : kind === 'milestone' && selMs ? <>, with <b>{selMs.name}</b>.</>
                  : <> — a committed date, not anchored to the plan.</>}
              </div>
              {preview && (
                preview.fc?.ready ? (
                  <>
                    <div>
                      Forecasting from <b>{preview.open} open card{preview.open === 1 ? '' : 's'}</b> — predicted{' '}
                      <b>{shortDay(new Date(preview.fc.p85).toISOString().slice(0, 10))}</b> · 85% confident.
                    </div>
                    {overBy > 0 && (
                      <div className="golive-anchor-warn">
                        Forecast is ~{overBy} day{overBy === 1 ? '' : 's'} past the planned end — the Plan header and the register will show this.
                      </div>
                    )}
                  </>
                ) : (
                  <div className="golive-anchor-muted">
                    {preview.open > 0 ? 'Not enough finished-work history yet to predict a date — the planned day stands.' : 'No open work mapped to this anchor yet.'}
                  </div>
                )
              )}
              {kind === 'fixed' && (
                <div className="golive-anchor-muted">A committed date keeps the planned-breach check: a phase scheduled to finish after it still flags.</div>
              )}
            </div>
          )}

          <div className="golive-anchor-actions">
            <button className="btn btn-primary btn-small" onClick={commit} disabled={!canCommit}>Anchor Go-Live</button>
            <button className="btn btn-small" onClick={dismiss}>Cancel</button>
          </div>
        </div>
      </div>
    </div>
  );
}

export function MilestonesCard({ goLiveDay, mayPlan }: { goLiveDay?: string; mayPlan: boolean }) {
  const inert = useSampleInert();
  const projectId = useApp((s) => s.projectId);
  const projects = useApp((s) => s.projects);
  const project = projects.find((p) => p.id === projectId);
  const phases = useApp((s) => s.phases);
  const milestones = useApp((s) => s.milestones);
  const issues = useApp((s) => s.issues);
  const workCalendar = useApp((s) => s.workCalendar);
  // null = nothing open; 'new' = the add form; an id = that row's edit form.
  const [editing, setEditing] = useState<null | 'new' | string>(null);

  const now = Date.now();
  // The register reads the SAME cadence forecast the Portfolio does, so a milestone's
  // predicted finish (p85) shows here on the plan where the dates are set — not only in
  // the roll-up (#4, 29 Oct). Cumulative scope in due order, so the parts add up to the
  // whole-board go-live exactly as the roll-up computes them.
  const list = useMemo(
    () =>
      project
        ? withMilestoneForecasts(
            projectMilestones(
              { id: project.id, name: project.name, planDay: goLiveDay, forecastDay: goLiveDay },
              phases,
              milestones,
              now,
              issues,
            ),
            issues,
            now,
            FORECAST_TRIALS_GLANCE,
            workCalendar,
          )
        : [],
    [project, phases, milestones, goLiveDay, now, issues, workCalendar],
  );
  const lines = project?.invoices ?? [];
  const recon = milestonesVsContract(list, project?.contractValue);

  if (!project) return null;

  return (
    <section className="plan2-ms">
      <div className="plan2-ms-head">
        <div className="plan2-ms-head-l">
          <h2 data-reveal="billable">Milestones</h2>
          <InfoTip label="Milestones">
            Dated points in the plan. Turn one <b>billable</b> to make it a payment stage — a bill value
            invoices can claim against, in full or in parts. The Go-Live and any promoted phase end read
            from the plan and carry no value.
          </InfoTip>
          <span className="plan2-ms-head-sub">Dated points &amp; payment stages</span>
        </div>
        {mayPlan && editing !== 'new' && (
          <button type="button" {...inert('plan2-ms-add', () => setEditing('new'))}>
            <Icon name="plus" size={15} /> Add milestone
          </button>
        )}
      </div>

      {editing === 'new' && (
        <MilestoneEditor onDone={() => setEditing(null)} onCancel={() => setEditing(null)} />
      )}

      {list.length === 0 && editing !== 'new' ? (
        <p className="plan2-ms-empty">
          No milestones yet.{mayPlan ? ' Add a dated point, or a billable payment stage, above.' : ''}
        </p>
      ) : (
        <ul className="plan2-ms-list">
          {list.map((m) =>
            editing === m.rowId && m.rowId ? (
              <li key={m.key}>
                <MilestoneEditor
                  milestone={m}
                  onDone={() => setEditing(null)}
                  onCancel={() => setEditing(null)}
                />
              </li>
            ) : (
              <li key={m.key}>
                <MilestoneRegisterRow
                  m={m}
                  lines={lines}
                  mayPlan={mayPlan}
                  onEdit={() => m.rowId && setEditing(m.rowId)}
                />
              </li>
            ),
          )}
        </ul>
      )}

      {/* The contract-reconciliation nudge — a guide, not a limit (mock 1d / 4c).
          Calm and never red; the only coloured figure is the amber "unallocated". */}
      {recon.contractValue != null && recon.billableTotal > 0 && (
        <div className="plan2-ms-recon">
          <span className="plan2-ms-recon-mark" aria-hidden="true"><Icon name="coin" size={18} /></span>
          <div className="plan2-ms-recon-main">
            <p className="plan2-ms-recon-line">
              Billable milestones total <b className="tnum">{fmtMoney(recon.billableTotal)}</b> of the{' '}
              <b className="tnum">{fmtMoney(recon.contractValue)}</b> contract
              {recon.unallocated != null && recon.unallocated !== 0 && (
                <>
                  {' '}&mdash;{' '}
                  <b className="tnum plan2-ms-recon-gap">
                    {fmtMoney(Math.abs(recon.unallocated))} {recon.unallocated > 0 ? 'unallocated' : 'over the contract'}
                  </b>
                </>
              )}
              .
            </p>
            <div className="plan2-ms-recon-bar" aria-hidden="true">
              <span
                className="plan2-ms-recon-fill"
                style={{ width: `${Math.min(100, (recon.billableTotal / recon.contractValue) * 100)}%` }}
              />
            </div>
          </div>
          <span className="plan2-ms-recon-guide">A guide, not a limit — milestones need not sum to the contract.</span>
        </div>
      )}
    </section>
  );
}

function MilestoneRegisterRow({
  m,
  lines,
  mayPlan,
  onEdit,
}: {
  m: Milestone;
  lines: import('../projects').InvoiceLine[];
  mayPlan: boolean;
  onEdit: () => void;
}) {
  const editable = !!m.rowId;
  const inert = useSampleInert();
  const setView = useApp((s) => s.setView);
  const rowPhases = useApp((s) => s.phases);
  const rowIssues = useApp((s) => s.issues);
  const rowAreas = useApp((s) => s.areas);
  // How the forecast tie reads on the row once set (0215): the work it gates + how many
  // cards, so saving a scope is visibly confirmed (never a silent change).
  const tieRead = useMemo(() => {
    const tie = m.forecastTie;
    if (!tie || !tie.phases.length) return null;
    const names = tie.phases.map((id) => rowPhases.find((p) => p.id === id)?.name ?? 'a phase');
    let where = names.join(' · ');
    if (tie.narrow && tie.phases.length === 1) {
      where += ` · ${tie.narrow.kind === 'area' ? (rowAreas.find((a) => a.id === tie.narrow!.id)?.name ?? tie.narrow.id) : tie.narrow.id}`;
    }
    return { where, count: (milestoneScope(m, rowIssues) ?? []).length };
  }, [m, rowPhases, rowIssues, rowAreas]);
  const billing =
    m.billable && m.billValue != null ? milestoneBilling(m.rowId as string, m.billValue, lines) : null;
  const badge = billing ? BILLING_BADGE[billing.state] : SCHEDULE_BADGE[m.state];
  // Whole local days the 85%-confident finish lands past the planned day — the same
  // slip read the Portfolio table shows, so the plan and the roll-up never disagree.
  const predSlip = (() => {
    const f = m.forecast;
    if (!f || !f.ready || !m.plannedDay) return 0;
    const [y, mo, d] = m.plannedDay.split('-').map(Number);
    const planMs = new Date(y, (mo || 1) - 1, d || 1).getTime();
    return Math.max(0, Math.round((f.p85 - planMs) / 86_400_000));
  })();
  const paidPct = billing && billing.scheduled > 0 ? Math.min(100, (billing.paid / billing.scheduled) * 100) : 0;
  const claimedPct =
    billing && billing.scheduled > 0
      ? Math.min(100 - paidPct, Math.max(0, ((billing.claimed - billing.paid) / billing.scheduled) * 100))
      : 0;

  return (
    <div className="plan2-ms-row">
      <div className="plan2-ms-row-top">
        <span className={`plan2-ms-glyph k-${m.kind}`} aria-hidden="true">
          <Icon name={KIND_ICON[m.kind]} size={18} />
        </span>
        <div className="plan2-ms-row-main">
          <div className="plan2-ms-titleline">
            <span className="plan2-ms-name">{m.name}</span>
            <span className="plan2-ms-kind">{MILESTONE_KIND_LABELS[m.kind]}</span>
            {m.billable && (
              <span className="plan2-ms-coin-tag">
                <Icon name="coin" size={13} /> Billable
              </span>
            )}
          </div>
          <div className="plan2-ms-dateline">
            <Icon name="calendar" size={14} />
            <span>{milestoneDay(m.actualDay ?? m.forecastDay ?? m.plannedDay) || 'No date'}</span>
            {/* The row shows the FORECAST when it has slipped past the planned date;
                say so, or it silently disagrees with the editor's "Target date"
                (the planned date) with only the badge to hint at it (UX-F4). */}
            {!m.actualDay && m.forecastDay != null && m.forecastDay !== m.plannedDay && (
              <span className="plan2-ms-datequal">forecast</span>
            )}
            {!editable && <span className="plan2-ms-fromplan">· from the plan — no payment attached</span>}
          </div>
          {tieRead && (
            <div className="plan2-ms-tieline">Gates {tieRead.where} · {tieRead.count} card{tieRead.count === 1 ? '' : 's'}</div>
          )}
          {/* The cadence prediction (#4) — the 85%-confident finish from the board's own
              pace, the same read the Portfolio leads with, shown here where the milestone
              is defined. Only a tied, un-landed milestone has a forecast; a landed or
              untied one shows nothing. */}
          {!m.actualDay && m.forecast && (
            m.forecast.ready ? (
              <div className="plan2-ms-predline">
                <Icon name="chart" size={13} />
                <span>
                  Pace predicts <b>{fmtScopeDay(m.forecast.p85)}</b> · 85% confident · 50/50 ~{fmtScopeDay(m.forecast.p50)}
                  {predSlip > 0 && <span className="plan2-ms-predslip"> · ~{predSlip} day{predSlip === 1 ? '' : 's'} past plan</span>}
                </span>
              </div>
            ) : (
              <div className="plan2-ms-predline plan2-ms-predmute">
                <Icon name="chart" size={13} />
                <span>Not enough finished history yet to predict from pace</span>
              </div>
            )
          )}
        </div>
        <div className="plan2-ms-row-right">
          <span className={`rag ${badge.cls}`}>
            <span className="dot" />
            {badge.word}
          </span>
          {editable && mayPlan && (
            <button type="button" {...inert('btn btn-small', onEdit)}>
              <Icon name="signature" size={14} /> Edit
            </button>
          )}
        </div>
      </div>

      {billing && (
        <div className="plan2-ms-sched">
          <div className="plan2-ms-schedbar" aria-hidden="true">
            <span className="plan2-ms-schedbar-paid" style={{ width: `${paidPct}%` }} />
            <span className="plan2-ms-schedbar-claimed" style={{ width: `${claimedPct}%` }} />
          </div>
          <div className="plan2-ms-schedcells">
            <div className="plan2-ms-cell">
              <span className="plan2-ms-cell-l">Scheduled</span>
              <span className="plan2-ms-cell-v tnum">{fmtMoney(billing.scheduled)}</span>
            </div>
            <div className="plan2-ms-cell">
              <span className="plan2-ms-cell-l">Claimed to date</span>
              <span className="plan2-ms-cell-v tnum">{fmtMoney(billing.claimed)}</span>
            </div>
            <div className="plan2-ms-cell">
              <span className="plan2-ms-cell-l">Paid</span>
              <span className="plan2-ms-cell-v tnum is-paid">{fmtMoney(billing.paid)}</span>
            </div>
            <div className="plan2-ms-cell">
              <span className="plan2-ms-cell-l">Remaining</span>
              <span className="plan2-ms-cell-v tnum">{fmtMoney(Math.max(0, billing.remaining))}</span>
            </div>
          </div>
          {/* The on-ramp the workflow witness found missing (WF-F1): a billable
              stage with value still to claim links straight to where the payment
              is raised, instead of leaving the reader to find Budget themselves. */}
          {editable && mayPlan && billing.remaining > 0 && (
            <div className="plan2-ms-schedfoot">
              <button type="button" className="link" onClick={() => setView('budgetpay')}>
                {billing.claimed > 0 ? 'Claim more in Budget & payments' : 'Raise a payment in Budget & payments'}
                <Icon name="chevron" size={14} />
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/** An epoch-ms instant as "9 Nov" — the forecast percentiles in the scope preview. */
const fmtScopeDay = (ms: number) => new Date(ms).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });

/** The labels for the Forecast-scope segmented control (0215). */
const SCOPE_MODES: { key: 'none' | 'one' | 'several' | 'part'; label: string }[] = [
  { key: 'none', label: 'From the plan' },
  { key: 'one', label: 'One phase' },
  { key: 'several', label: 'Several phases' },
  { key: 'part', label: 'Part of a phase' },
];

/** Build the stored tie from the editor's scope state — null when there is no tie
 *  (the milestone reads "from the plan"). Pure, so the save path and the live preview
 *  read the same thing. */
function buildForecastTie(
  mode: 'none' | 'one' | 'several' | 'part',
  onePhase: string,
  severalPhases: string[],
  narrowKind: '' | 'area' | 'epic',
  narrowId: string,
): MilestoneTie | null {
  if (mode === 'several') return severalPhases.length ? { phases: [...severalPhases] } : null;
  if (mode === 'one') return onePhase ? { phases: [onePhase] } : null;
  if (mode === 'part') {
    if (!onePhase) return null;
    return narrowKind && narrowId ? { phases: [onePhase], narrow: { kind: narrowKind, id: narrowId } } : { phases: [onePhase] };
  }
  return null;
}

function MilestoneEditor({
  milestone,
  onDone,
  onCancel,
}: {
  milestone?: Milestone;
  onDone: () => void;
  onCancel: () => void;
}) {
  const createMilestone = useApp((s) => s.createMilestone);
  const editMilestone = useApp((s) => s.editMilestone);
  const deleteMilestone = useApp((s) => s.deleteMilestone);
  // The live tie preview must forecast on the SAME working calendar the register and the
  // anchor editor use, or its predicted date reads days earlier (it would price in weekends
  // and closures the real surfaces skip) — a planner would set a tie against a wrong number.
  const workCalendar = useApp((s) => s.workCalendar);
  const [name, setName] = useState(milestone?.name ?? '');
  const [kind, setKind] = useState<MilestoneKind>(milestone?.kind ?? 'delivery');
  const [targetDay, setTargetDay] = useState(milestone?.plannedDay ?? '');
  const [billable, setBillable] = useState(!!milestone?.billable);
  const [billValue, setBillValue] = useState<number | undefined>(milestone?.billValue);
  const [says, setSays] = useState('');
  const [busy, setBusy] = useState(false);
  const editing = !!milestone?.rowId;

  // ── Forecast scope (0215): the work this checkpoint gates ──
  const phases = useApp((s) => s.phases);
  const issues = useApp((s) => s.issues);
  const areas = useApp((s) => s.areas);
  const phaseOpts = useMemo(() => phases.filter((p) => !p.retiredAt), [phases]);
  const tie0 = milestone?.forecastTie;
  const [scopeMode, setScopeMode] = useState<'none' | 'one' | 'several' | 'part'>(
    !tie0 ? 'none' : tie0.phases.length > 1 ? 'several' : tie0.narrow ? 'part' : 'one',
  );
  const [onePhase, setOnePhase] = useState(tie0 && tie0.phases.length ? tie0.phases[0] : '');
  const [severalPhases, setSeveralPhases] = useState<string[]>(tie0 && tie0.phases.length > 1 ? tie0.phases : []);
  const [narrowKind, setNarrowKind] = useState<'' | 'area' | 'epic'>(tie0?.narrow?.kind ?? '');
  const [narrowId, setNarrowId] = useState(tie0?.narrow?.id ?? '');

  // The areas and epics actually present on the chosen phase's cards — so a "part of
  // a phase" narrow only ever offers a grouping that gates real work.
  const narrowOpts = useMemo(() => {
    if (scopeMode !== 'part' || !onePhase) return [] as { kind: 'area' | 'epic'; id: string; label: string }[];
    const out: { kind: 'area' | 'epic'; id: string; label: string }[] = [];
    const seenA = new Set<string>();
    const seenE = new Set<string>();
    for (const i of issues) {
      if (i.phase !== onePhase) continue;
      if (i.area && !seenA.has(i.area)) { seenA.add(i.area); out.push({ kind: 'area', id: i.area, label: `Area: ${areas.find((a) => a.id === i.area)?.name ?? i.area}` }); }
      if (i.epic && !seenE.has(i.epic)) { seenE.add(i.epic); out.push({ kind: 'epic', id: i.epic, label: `Epic: ${i.epic}` }); }
    }
    return out;
  }, [scopeMode, onePhase, issues, areas]);

  // A live read of what the current tie gates and its prediction, so the scope lands
  // before saving. The real engine, light trials; name is deliberately not a dep.
  const preview = useMemo(() => {
    const tie = buildForecastTie(scopeMode, onePhase, severalPhases, narrowKind, narrowId);
    if (!tie) return null;
    const ghost: Milestone = { key: 'ms:preview', name: '', kind, boardId: '', projectName: '', state: 'on-track', slipDays: 0, plannedDay: targetDay || undefined, forecastTie: tie };
    const scope = milestoneScope(ghost, issues) ?? [];
    const f = milestoneForecast(ghost, issues, Date.now(), FORECAST_TRIALS_GLANCE, workCalendar);
    const names = tie.phases.map((id) => phaseOpts.find((p) => p.id === id)?.name ?? 'a phase');
    let where = names.join(' · ');
    if (tie.narrow && tie.phases.length === 1) where += ` · ${tie.narrow.kind === 'area' ? (areas.find((a) => a.id === tie.narrow!.id)?.name ?? tie.narrow.id) : tie.narrow.id}`;
    return { count: scope.length, where, f };
  }, [scopeMode, onePhase, severalPhases, narrowKind, narrowId, kind, targetDay, issues, areas, phaseOpts, workCalendar]);

  const save = async () => {
    setBusy(true);
    const forecastTie = buildForecastTie(scopeMode, onePhase, severalPhases, narrowKind, narrowId);
    const why = editing
      ? await editMilestone(milestone.rowId as string, {
          name,
          kind,
          targetDay: targetDay || null,
          billable,
          billValue: billable ? billValue ?? null : null,
          forecastTie,
        })
      : await createMilestone({
          name,
          kind,
          targetDay: targetDay || null,
          billable,
          billValue: billable ? billValue ?? null : null,
          forecastTie,
        });
    setBusy(false);
    if (why) {
      setSays(why);
      return;
    }
    onDone();
  };

  return (
    <div className="plan2-ms-editor">
      <div className="plan2-ms-editor-head">{editing ? 'Edit milestone' : 'New milestone'}</div>
      <div className="plan2-ms-editor-grid">
        <label className="stack">
          <span>Name <span className="req">*</span></span>
          <input
            className="input"
            autoFocus
            required
            aria-required="true"
            value={name}
            placeholder="e.g. Build acceptance"
            onChange={(e) => { setName(e.target.value); setSays(''); }}
          />
        </label>
        <label className="stack">
          <span>Type</span>
          <select className="input" value={kind} onChange={(e) => setKind(e.target.value as MilestoneKind)}>
            {MILESTONE_KINDS.map((k) => (
              <option key={k} value={k}>{MILESTONE_KIND_LABELS[k]}</option>
            ))}
          </select>
        </label>
        <label className="stack">
          <span>Target date</span>
          <DateField
            ariaLabel="Target date"
            variant="form"
            value={targetDay}
            onChange={(v) => setTargetDay(v)}
          />
        </label>
      </div>

      <label className="plan2-ms-billable">
        <span
          className={`plan2-ms-switch ${billable ? 'is-on' : ''}`}
          role="switch"
          aria-checked={billable}
          tabIndex={0}
          onClick={() => setBillable((b) => !b)}
          onKeyDown={(e) => { if (e.key === ' ' || e.key === 'Enter') { e.preventDefault(); setBillable((b) => !b); } }}
        >
          <span className="plan2-ms-switch-knob" />
        </span>
        <span className="plan2-ms-billable-text">
          <span className="plan2-ms-billable-title">Billable</span>
          <span className="plan2-ms-billable-sub">Attach a payment that invoices can claim against</span>
        </span>
      </label>

      {billable && (
        <label className="stack plan2-ms-billvalue">
          <span>Bill value <span className="req">*</span></span>
          <MoneyInput value={billValue} onChange={setBillValue} ariaLabel="Bill value" required placeholder="e.g. 240000" />
          <span className="hint">The amount this stage is worth. Invoices claim against it — in full or in parts.</span>
        </label>
      )}

      <div className="plan2-ms-scope">
        <div className="plan2-ms-scope-head">
          Forecast scope
          <InfoTip label="Forecast scope">
            A milestone is a checkpoint — separate from a phase, and a phase can hold several. Tie it to the
            work it gates and the portfolio predicts when it will be met from the board&rsquo;s cadence: one
            phase, several phases, or part of a phase (one phase, narrowed to an area or epic). Leave it on
            <b> From the plan</b> and the milestone keeps reading its planned date, as before.
          </InfoTip>
        </div>
        <div className="seg plan2-ms-scope-seg" role="tablist" aria-label="Forecast scope">
          {SCOPE_MODES.map((sm) => (
            <button
              key={sm.key}
              type="button"
              role="tab"
              aria-selected={scopeMode === sm.key}
              className={`seg-btn ${scopeMode === sm.key ? 'active' : ''}`}
              onClick={() => setScopeMode(sm.key)}
            >
              {sm.label}
            </button>
          ))}
        </div>

        {(scopeMode === 'one' || scopeMode === 'part') && (
          <label className="stack plan2-ms-scope-phase">
            <span>Phase</span>
            <select className="input" value={onePhase} onChange={(e) => { setOnePhase(e.target.value); setNarrowKind(''); setNarrowId(''); }}>
              <option value="">Choose a phase…</option>
              {phaseOpts.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </label>
        )}

        {scopeMode === 'several' && (
          <div className="plan2-ms-scope-phases">
            {phaseOpts.length === 0 && <span className="hint">This board has no phases yet.</span>}
            {phaseOpts.map((p) => (
              <label key={p.id} className="plan2-ms-scope-check">
                <input
                  type="checkbox"
                  checked={severalPhases.includes(p.id)}
                  onChange={(e) => setSeveralPhases((prev) => (e.target.checked ? [...prev, p.id] : prev.filter((x) => x !== p.id)))}
                />
                <span>{p.name}</span>
              </label>
            ))}
          </div>
        )}

        {scopeMode === 'part' && onePhase && (
          <div className="plan2-ms-scope-narrow">
            <span className="plan2-ms-scope-narrow-lab">Narrow to <span className="plan2-ms-scope-opt">— optional; leave empty for the whole phase</span></span>
            {narrowOpts.length === 0 ? (
              <span className="hint">No areas or epics on this phase&rsquo;s cards yet — it will gate the whole phase.</span>
            ) : (
              <div className="plan2-ms-scope-chips">
                {narrowOpts.map((o) => {
                  const on = narrowKind === o.kind && narrowId === o.id;
                  return (
                    <button
                      key={`${o.kind}:${o.id}`}
                      type="button"
                      className={`plan2-ms-scope-chip ${on ? 'active' : ''}`}
                      onClick={() => { if (on) { setNarrowKind(''); setNarrowId(''); } else { setNarrowKind(o.kind); setNarrowId(o.id); } }}
                    >
                      {o.label}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {preview && (
          <div className="plan2-ms-scope-read">
            Forecasting from <b>{preview.count} card{preview.count === 1 ? '' : 's'}</b> in <b>{preview.where}</b>
            {preview.f && preview.f.ready
              ? <> — <b>{fmtScopeDay(preview.f.p85)}</b> · 85% confident · 50/50 ~{fmtScopeDay(preview.f.p50)}</>
              : <> — not enough finished history yet to predict</>}
          </div>
        )}
      </div>

      {says && <p className="plan2-err" role="alert">{says}</p>}

      <div className="plan2-ms-editor-foot">
        <button
          className="btn btn-primary btn-small"
          disabled={!name.trim() || (billable && !(billValue && billValue > 0)) || busy}
          onClick={() => void save()}
        >
          {editing ? 'Save milestone' : 'Add milestone'}
        </button>
        <button className="btn btn-small" onClick={onCancel}>Cancel</button>
        {editing && (
          <button
            type="button"
            className="btn btn-small plan2-ms-remove"
            onClick={async () => {
              const ok = await confirmDialog({
                title: `Remove the “${milestone.name}” milestone?`,
                body:
                  'It stops being a point on the plan. Any invoice lines billed against it keep their amount but no longer claim a stage.',
                confirmLabel: 'Remove',
                tone: 'danger',
              });
              if (!ok) return;
              const why = await deleteMilestone(milestone.rowId as string);
              if (why) setSays(why);
              else onDone();
            }}
          >
            Remove
          </button>
        )}
      </div>
    </div>
  );
}

function RetiredPhases({ retired }: { retired: Phase[] }) {
  const inert = useSampleInert();
  const restorePhase = useApp((s) => s.restorePhase);
  const [says, setSays] = useState('');
  return (
    <section className="panel plan2-retired">
      <h2>Retired Phases</h2>
      <p className="plan2-retired-note">
        Removing a phase does not clear it off the cards running in it — it stops being offered on new
        work and stays where it already is, so a report drawn last month still adds up.
      </p>
      <ul className="plan2-retired-list">
        {retired.map((p) => (
          <li key={p.id} className="plan2-retired-row">
            <span className="plan2-name-flat">{p.name}</span>
            <button {...inert('btn btn-small', async () => setSays(await restorePhase(p.id)))}>
              Put back
            </button>
          </li>
        ))}
      </ul>
      {says && (
        <p className="plan2-err" role="alert">
          {says}
        </p>
      )}
    </section>
  );
}
